/**
 * The per-entity dual-wield state machine.
 *
 * Two hands run independent combo state on purpose. If they shared a timer the
 * "conversation" between main and off hand collapses into a single attack
 * button with two skins — the off-hand has to be able to interject mid-recovery
 * for priming/detonation to feel like a dialogue rather than a rotation.
 *
 * Three rules from docs/02 §4 are load-bearing and are enforced here rather
 * than trusted to the data:
 *   - attacks apply drag to horizontal velocity, never zero it;
 *   - hitstop is symmetric, short, and does not consume buffered input;
 *   - hit-confirm refreshes Airstep, which is why `onHitConfirm` is a required
 *     callback rather than an optional hook — movement depends on it.
 */

import { clamp } from '../core/math';
import { Rng } from '../core/rng';
import { BodyFlag, Comp, Team, World } from '../entity/ecs';
import { aimAngleFor } from '../core/input';
import type { Input } from '../core/input';
import type { CellWorld } from '../sim/world';
import { M, VOID } from '../sim/materials';
import {
  BASE_STAT, newHitResult, resolveHit, resolveWeapon, applyTerrain, collectTriggerMods,
} from './resolve';
import type { HitInput, HitResult, TriggerMods } from './resolve';
import { applyStatus } from './status';
import { harvestSwing } from './harvest';
import type { MaterialPouch } from './harvest';
import {
  CONTINUOUS_HIT_MS, ammoCostOf, attackDrag, cameraKick, chargeKindOf,
  isContinuous, magazineOf, nodeTiming,
} from './weaponData';
import type { ChargeKind } from './weaponData';
import { StatusKind, TriggerFlag, WeaponTag } from './types';
import type {
  ComboNode, CombatStats, Hand, HitboxShape, ModuleDef, PrimerKindId,
  ResolvedWeapon, RolledStats, TriggerMask, WeaponDef, WeaponInstance,
} from './types';

// ------------------------------------------------------------------ tuning

/** Input buffer window for starting an attack. Matches the jump buffer. */
const ATTACK_BUFFER_MS = 120;
const DASH_BUFFER_MS = 110;
/** How long a finished node stays chainable before the combo resets. */
const CHAIN_WINDOW_MS = 420;
/** Holding the off-hand past this enters stance instead of attacking. */
const STANCE_HOLD_MS = 165;
/** Rhythm gained by alternating hands, and lost per second while idle. */
const RHYTHM_GAIN = 0.24;
const RHYTHM_DECAY = 0.42;
/** Alternation only counts if the other hand swung this recently. */
const RHYTHM_WINDOW_MS = 900;
/** Pogo rebound speed (docs/02 §4). */
const POGO_SPEED = 320;
/** Max targets a single node may hit before it stops connecting. */
const MAX_HITS_PER_NODE = 8;
// `CONTINUOUS_HIT_MS` is imported from `weaponData.ts`. It used to be declared
// here, unexported, with a second copy of the same 100 in `weaponData.ts`
// calling itself "matched to" it and nothing asserting the match.
/** How long an `onKill` / `onParry` / `onSwap` trigger stays armed, ms. */
const TRIGGER_WINDOW_MS = 2500;

// -------------------------------------------------------- resources (§11)
//
// The reason this layer exists is not the HUD. It is that a projectile with no
// cost is strictly better than melee at range — same damage model, no resource,
// no downside — so there is never a reason to close the distance. Ammo is the
// price of range; heat is the price of *sustained* range; charge is the three
// per-class identities docs/04 §3 already promised and never delivered.

/** Rounds a connected melee hit hands back to the other hand (docs/04 §8). */
const AMMO_PER_MELEE_HIT = 1;
/** Heat shed per second whenever the hand is not in windup or active. */
const HEAT_COOL_PER_SEC = 0.32;
/**
 * Heat must fall back to here before a cooked hand may fire again.
 *
 * A lockout that clears the instant heat drops below 1 is not a lockout: the
 * weapon would stutter at the ceiling and keep firing. The gap is the penalty.
 */
const HEAT_CLEAR = 0.55;
/** Drill fuel burned per second of grinding, and recovered per second at rest. */
const FUEL_DRAIN_PER_SEC = 0.22;
const FUEL_REGEN_PER_SEC = 0.11;
/** Fuel needed to start a fresh grind, so an empty tank cannot be feathered. */
const FUEL_RESTART = 0.15;
/** Living-weapon hunger: it grows on the clock and is fed by kills (§3.1). */
const HUNGER_PER_SEC = 0.035;
const HUNGER_PER_KILL = 0.28;
/** Health per second the symbiote takes out of its wielder once starved. */
const HUNGER_BITE_DPS = 4;
/**
 * Fraction of a chain weapon's spin that survives the release.
 *
 * Retention is what makes it a flywheel rather than a hold-and-release: the
 * second spin-up starts from where the first left off, so a chain user who
 * keeps swinging keeps the radius, and one who disengages loses it.
 */
const SPIN_RETAIN = 0.4;
/** Spin bled off per second while the weapon is idle. */
const SPIN_DECAY_PER_SEC = 0.35;

// ------------------------------------------------------------ class identity
//
// docs/04 §3 authors a signature behaviour per class and, until now, exactly one
// of them had code (`class === 'dagger'` for backstab). Eight are hand-written
// here — drill fuel and chain spin-up above, and spear, whip, shield, living,
// relic blade and drone below — and the other 20 get their identity from data.
// Issue #16 §"the twenty-one others" is the ruling that makes that a decision
// rather than an omission, and §3.1/§3.2 of the doc now say which is which.
//
// The last two were sequenced last on purpose: each needed a *system* rather
// than a behaviour. The drone needed summons (`combat/summons.ts`), because a
// destructible entity is not something a `FireSpec` can describe; the relic
// blade needed worldgen, because "the moveset changes with the biome" means
// nothing until biomes are an ordered ramp rather than a set of room flavours.

/** Speed a spear planted in terrain throws its wielder upward, units/sec. */
const VAULT_SPEED = 300;
/** Forward carry off the plant. A vault is a *traversal* move, not a jump. */
const VAULT_PUSH = 90;
/** Speed the whip reels a grabbed body in at. */
const GRAB_REEL = 190;
/**
 * A body this many times the wielder's mass is an anchor, not a catch: the
 * tether pulls *you* instead. That is the same call `abilities.ts` makes about
 * terrain, applied to bodies, and it is what stops the whip being a knockback
 * weapon with the sign flipped.
 *
 * Exported since #122: the attraction field asks the same question of the same
 * numbers — a full-strength field beside something too heavy hauls its owner
 * instead of hauling the world — and a second constant with the same value
 * would be two rules that agree until one of them is tuned.
 */
export const TETHER_MASS_RATIO = 2.5;
/** Guard spent per blocked blow, and regained per second with the shield down. */
const GUARD_PER_BLOCK = 0.3;
const GUARD_REGEN_PER_SEC = 0.35;
/** Stagger the wielder eats when the guard is broken through, seconds. */
const GUARD_BREAK_STAGGER = 0.5;
/**
 * Hunger at which the symbiote's moveset mutates.
 *
 * "Its moveset mutates either way" is the second half of the Hungering
 * Symbiote's own blurb and the half that had no code. Above this the light
 * opener is the wide finisher and the chain runs *down* from it instead of up —
 * the same button, a different weapon, decided by a resource. No other class
 * changes which node an input selects.
 */
const MUTATE_HUNGER = 0.55;
/** What a delayed duplicate strike is worth, per the Echo Core's own summary. */
const ECHO_DAMAGE_FRAC = 0.6;
/** Echoes owed at once. Past this the oldest is overwritten rather than dropped. */
const MAX_ECHOES = 12;

/** One owed duplicate strike. Pooled and reused; see `WielderState.echoes`. */
interface EchoStrike {
  active: boolean;
  /** ms left before it lands. */
  timer: number;
  target: number;
  hand: Hand;
  /** Damage scalar of the blow being repeated, times `ECHO_DAMAGE_FRAC`. */
  damageMul: number;
}

export type HandPhase =
  | 'idle' | 'charging' | 'windup' | 'active' | 'recovery' | 'parry' | 'reloading';

export interface HandState {
  hand: Hand;
  instance: WeaponInstance | null;
  weapon: ResolvedWeapon | null;
  node: ComboNode | null;
  phase: HandPhase;
  /** ms elapsed inside the current phase. */
  timer: number;
  /** ms since the node finished; drives the chain window. */
  sinceEnd: number;
  /** Node the last completed attack chains from. */
  chainFrom: ComboNode | null;
  /** ms the attack button has been held, for charge states. */
  chargeHeld: number;
  /** Charge damage multiplier locked in at release. */
  chargeMul: number;
  /** Charge hitbox multiplier locked in at release — the chain's spin-up radius. */
  chargeRadiusMul: number;
  /** Primer the charge tier stamps on whatever the release connects with. */
  chargePrimer: PrimerKindId | 0;
  /**
   * Which entry class the press opened with, remembered across the hold.
   * Without this a charged press always released into `light` or `heavy`, so
   * the four chargeable classes had no dash-attack, no air-attack and no pogo —
   * three universal rules of docs/04 §4 broken by 4 of 29 weapons.
   */
  chargeEntry: 'light' | 'heavy' | 'dash' | 'air' | 'dair';
  /** True once this node has connected with anything. */
  confirmed: boolean;
  /** Entities already hit by the current node. */
  hitCount: number;
  hits: Int32Array;
  /** Timestamp (ms, accumulated) of the last attack start. */
  lastStart: number;
  /** Gravity scale to restore when the node ends; NaN when not overridden. */
  savedGravity: number;
  /** Set when the node's movement impulse has already been applied. */
  impulsed: boolean;
  /** Set once this node has planted a spear in terrain, so a vault fires once. */
  vaulted: boolean;
  /** Extra light-chain hits already spent on the current chain (§5.3). */
  chainExtra: number;
  /** ms until this hand may re-contact, for continuous weapons only. */
  contactTimer: number;
  /** ms left in the reload; only meaningful in the `reloading` phase. */
  reloadTimer: number;
  /** ms of sustained fire not yet billed against the magazine (continuous). */
  ammoTimer: number;
  /** Set when heat hit the ceiling; cleared once it falls back to `HEAT_CLEAR`. */
  heatLocked: boolean;
  /**
   * Where this hand's live swing was aimed, latched from `WielderState.aim` at
   * `startNode`. `NaN` means the player held no vertical, so the node keeps its
   * authored angle.
   *
   * Latched rather than sampled live because a swing is a commitment: an arc
   * that swivelled to follow the stick halfway through would let a player redirect
   * a blow they had already thrown, and would tear the hitbox away from the
   * animation the renderer is already partway through drawing.
   */
  aim: number;
  /**
   * A thrown verb this hand has begun and nobody has drawn yet — issue #142.
   *
   * Raised by `startNode` and `startParry`, and lowered by `takeSwingStart`,
   * which is the *only* reader. It is a latch and not a derived predicate
   * because the thing it reports is an **edge**, and an edge that is derived
   * from state can only be seen by whoever looks on the right substep. See
   * `takeSwingStart` for the measurement that made this a field.
   */
  swingPending: boolean;
}

export interface WielderState {
  main: HandState;
  off: HandState;
  /** 0..1 — alternating hands fills it, spamming one does not (docs/04 §8). */
  rhythm: number;
  lastHand: Hand | null;
  /** Accumulated ms; the two hands compare timestamps against this. */
  clock: number;
  /** Off-hand modifier held: the main hand's moveset is remapped. */
  stance: boolean;
  /** Jump-cancel is only legal after a hit-confirm (docs/02 §4). */
  canJumpCancel: boolean;
  stats: CombatStats;
  /**
   * Where the player is currently pointing, as a local-frame angle, or `NaN`
   * for neutral. Sampled once per `updateWielder` from the held directions.
   *
   * **This is the only place aim enters combat.** Everything directional a
   * weapon does — the melee sweep, the projectile, the summon, the terrain cut,
   * the trail, the spear's plant — is derived from one rotation of the hitbox
   * frame in `fireOrSwing`, computed from this. Adding aim to any of those
   * individually is how they drift apart, which is this codebase's
   * most-repeated defect.
   */
  aim: number;
  /** Set true by the pogo path so movement can read it without a callback. */
  pogoed: boolean;
  /**
   * Set true on the substep a spear planted in terrain launched its wielder.
   * Read the same way `pogoed` is: combat has already written the velocity, and
   * this is how presentation and the mover's dash budget learn it happened.
   */
  vaulted: boolean;
  /**
   * Delayed duplicate strikes owed by `melee.echoDelayMs` (docs/04 §5.2).
   *
   * A fixed pool rather than a growing array: this is written from the hit path
   * several times per frame, and the temporal family's whole premise cannot be
   * the one thing in combat that allocates.
   */
  echoes: EchoStrike[];
  /**
   * Trigger events that are windows rather than properties of a contact.
   * ms remaining, indexed by the `TriggerFlag` they arm.
   */
  killWindow: number;
  parryWindow: number;
  swapWindow: number;
  /** ms of parry window still open on each hand; 0 when not parrying. */
  parryActive: number;
  /** Hand whose parry is open, for the counter node. */
  parryHand: Hand;
  /**
   * How long `down` has been continuously held, in ms — issue #139.
   *
   * `downHeld` is a *level*, and it was being read as two things at once: the
   * crouch stance (`movement.ts`'s `wantsCrouch`) and the heavy entry
   * (`pickEntryKind`). Two levels, one input, so the two could never be
   * combined and there was no way to throw a light attack while crouched. This
   * is the edge the level was missing; see `HEAVY_WINDOW_MS`.
   *
   * Derived from the axis, so it is one number on a stick and on a keyboard —
   * #96's ruling. Accumulated airborne as well as grounded, because it measures
   * the *input*, not the posture: down held through a jump is still a crouch
   * the moment you land.
   */
  downMs: number;
}

export interface WielderCtx {
  weapons: ReadonlyMap<string, WeaponDef>;
  modules: ReadonlyMap<string, ModuleDef>;
  rng: Rng;
  /**
   * Called on every connected hit. Movement uses this to refresh dash and
   * Airstep; presentation uses it for flash and particles.
   */
  onHitConfirm: (state: WielderState, hand: Hand, target: number, result: HitResult) => void;
  /** Projectile spawning is not this system's job — it delegates. */
  onFire?: (e: FireEvent) => void;
  /**
   * Summon deployment, for the one class whose payload is an entity. Optional
   * for the same reason `onFire` is: a headless rig must be able to run the
   * hand state machine without an entity system attached.
   */
  onSummon?: (e: SummonEvent) => void;
  onCameraKick?: (amount: number, dirX: number, dirY: number) => void;
  /**
   * Biome id underfoot, for the one class that asks (docs/04 §3.1, relic
   * blade). A callback rather than a field because the answer changes as the
   * player walks and combat must not hold a stale copy of where it is.
   *
   * Plain `string`, not a `BiomeId`: combat does not import the world's
   * taxonomy, exactly as `meta/unlockEffects.ts` does not import combat's. The
   * table below is keyed on strings and an id it has never heard of degrades to
   * the plain moveset.
   */
  biomeId?: () => string;
  /**
   * The run's material pouch, for the scythe's corpse harvest (`harvest.ts`,
   * issue #8). A callback for the same reason `biomeId` is one: the run object
   * is replaced on every death and combat must not hold a stale pouch that
   * silently swallows a player's materials.
   *
   * Optional, like `onFire` and `onSummon`: a headless rig has no run, and the
   * hand state machine must stay runnable without one. Returning `null` is the
   * ordinary answer outside a run.
   */
  pouch?: () => MaterialPouch | null;
}

/**
 * Everything a projectile spawner needs, in one reused struct.
 *
 * The old signature omitted the wielder's entity index and the resolved weapon,
 * which is a large part of why nobody ever implemented against it: a callback
 * that cannot say who fired or with what cannot spawn a round that resolves.
 */
export interface FireEvent {
  state: WielderState;
  hand: Hand;
  /** Entity index of the shooter. */
  attacker: number;
  weapon: ResolvedWeapon;
  node: ComboNode;
  /** Muzzle position, world units. */
  x: number;
  y: number;
  dirX: number;
  dirY: number;
  charge: number;
  chargePrimer: PrimerKindId | 0;
  windows: TriggerMask;
}

const scratchFire: FireEvent = {
  state: null as unknown as WielderState,
  hand: 'main',
  attacker: 0,
  weapon: null as unknown as ResolvedWeapon,
  node: null as unknown as ComboNode,
  x: 0, y: 0, dirX: 1, dirY: 0,
  charge: 1, chargePrimer: 0, windows: 0,
};

/**
 * What a summoner's press hands over — the drone class's counterpart to
 * `FireEvent` (docs/04 §3.2).
 *
 * A separate event rather than a flag on `FireEvent` because the two produce
 * different *kinds* of thing: a round belongs to the projectile pool and is
 * gone in a second, a drone is an entity in the ECS with its own health. Making
 * them one event would mean every `onFire` implementation had to know how to
 * refuse half of it.
 */
export interface SummonEvent {
  state: WielderState;
  hand: Hand;
  /** Entity index of the summoner. */
  owner: number;
  weapon: ResolvedWeapon;
  node: ComboNode;
  /** Deploy position, world units. */
  x: number;
  y: number;
  dirX: number;
  dirY: number;
  /** How many to put out — the node's `fire.count` folded with `countAdd`. */
  count: number;
  charge: number;
}

const scratchSummon: SummonEvent = {
  state: null as unknown as WielderState,
  hand: 'main',
  owner: 0,
  weapon: null as unknown as ResolvedWeapon,
  node: null as unknown as ComboNode,
  x: 0, y: 0, dirX: 1, dirY: 0,
  count: 1, charge: 1,
};

// ------------------------------------------------------------ construction

function makeHand(hand: Hand): HandState {
  return {
    hand,
    instance: null,
    weapon: null,
    node: null,
    phase: 'idle',
    timer: 0,
    sinceEnd: Infinity,
    chainFrom: null,
    chargeHeld: 0,
    chargeMul: 1,
    chargeRadiusMul: 1,
    chargePrimer: 0,
    chargeEntry: 'light',
    confirmed: false,
    hitCount: 0,
    hits: new Int32Array(MAX_HITS_PER_NODE),
    lastStart: -Infinity,
    savedGravity: NaN,
    impulsed: false,
    vaulted: false,
    chainExtra: 0,
    contactTimer: 0,
    reloadTimer: 0,
    ammoTimer: 0,
    heatLocked: false,
    aim: NaN,
    swingPending: false,
  };
}

/**
 * The unmodified body: every stat at the baseline, no investment anywhere.
 *
 * These were all zero, and `statScaling` reads them as an absolute, so every
 * `scaling` block on all 29 weapons was inert. They are `BASE_STAT` rather than
 * zero because `statScaling` now measures *deviation from baseline* — see the
 * comment there. That keeps today's damage numbers identical while making the
 * axis live the moment progression grants a point.
 */
export function defaultStats(): CombatStats {
  return {
    might: BASE_STAT, finesse: BASE_STAT, focus: BASE_STAT,
    resonance: BASE_STAT, vitality: BASE_STAT, tech: BASE_STAT,
    critChance: 0, critMul: 1, power: 1,
  };
}

/**
 * A stat block for a character `level` levels in, with `spent` points spread
 * across the six stats. The intended `createWielder` argument.
 */
export function statsForLevel(level = 1, spent: Partial<Record<keyof CombatStats, number>> = {}): CombatStats {
  const s = defaultStats();
  // Every level lifts the whole block a little; spent points steer it.
  const drift = (level - 1) * 0.12;
  s.might += drift + (spent.might ?? 0);
  s.finesse += drift + (spent.finesse ?? 0);
  s.focus += drift + (spent.focus ?? 0);
  s.resonance += drift + (spent.resonance ?? 0);
  s.vitality += drift + (spent.vitality ?? 0);
  s.tech += drift + (spent.tech ?? 0);
  return s;
}

export function createWielder(stats: CombatStats = defaultStats()): WielderState {
  return {
    main: makeHand('main'),
    off: makeHand('off'),
    rhythm: 0,
    lastHand: null,
    clock: 0,
    stance: false,
    canJumpCancel: false,
    stats,
    aim: NaN,
    pogoed: false,
    vaulted: false,
    echoes: Array.from({ length: MAX_ECHOES }, () => ({
      active: false, timer: 0, target: -1, hand: 'main' as Hand, damageMul: 1,
    })),
    killWindow: 0,
    parryWindow: 0,
    swapWindow: 0,
    parryActive: 0,
    parryHand: 'off',
    downMs: 0,
  };
}

// ------------------------------------------------------------ instance ids

/**
 * Monotonic counter behind `WeaponInstance.id`.
 *
 * Deliberately *not* derived from the rng. Instance ids have exactly one job —
 * telling two drops of the same definition apart — and two rng streams seeded
 * the same way produce the same numbers, which is normal and useful everywhere
 * else in this codebase and fatal here: `rollWeapon('flense_dagger', new Rng(3))`
 * called twice would mint one id for two weapons, which is the bug this is
 * supposed to fix. A counter cannot collide.
 */
let instanceCounter = 0;

/**
 * Mint an id. `defId#n` rather than a bare number so a log line, a save file
 * and a breakpoint all say what the weapon is as well as which one it is.
 */
export function nextInstanceId(defId: string): string {
  return `${defId}#${++instanceCounter}`;
}

/**
 * Raise the counter past an id read from a save.
 *
 * Loading a run restores instances that were minted in a previous session while
 * the counter is back at zero, so without this the next drop of the run reuses
 * a live id and the two weapons become one row again — the original bug,
 * reintroduced by persistence. `save.ts` must call this for every instance it
 * hydrates, before anything rolls a new one.
 */
export function noteInstanceId(id: string): void {
  const n = Number.parseInt(id.slice(id.lastIndexOf('#') + 1), 10);
  if (Number.isFinite(n) && n > instanceCounter) instanceCounter = n;
}

/** Test seam. Nothing in the game calls this; a fresh process starts at zero. */
export function resetInstanceIds(): void {
  instanceCounter = 0;
}

/** Roll a drop. Deterministic given the rng stream, except for the id. */
export function rollWeapon(defId: string, rng: Rng, level = 1): WeaponInstance {
  const rolled: RolledStats = {
    seed: rng.u32(),
    damageMul: rng.range(0.92, 1.12),
    cadenceMul: rng.range(0.95, 1.08),
    reachMul: rng.range(0.97, 1.05),
    weightAdd: rng.range(-0.05, 0.05),
    critChanceAdd: rng.range(0, 0.06),
    critMulAdd: rng.range(0, 0.25),
  };
  return {
    id: nextInstanceId(defId),
    defId, modules: [], rolled, level, favorite: false,
    // -1 is "never folded": the magazine size and the starting charge are
    // properties of the definition, and this function has only the id.
    // `resolveWeapon` racks both the first time it sees the instance.
    charge: -1, heat: 0, ammo: -1,
  };
}

/** Install an instance into a hand, folding its modules in once. */
export function equip(
  state: WielderState,
  hand: Hand,
  instance: WeaponInstance | null,
  ctx: WielderCtx,
): void {
  const h = hand === 'main' ? state.main : state.off;
  cancelNode(h, null, 0);
  h.instance = instance;
  // Resource *state* lives on the instance and travels with it; resource
  // *timers* belong to the hand, so a half-finished reload does not follow one
  // weapon into the slot the next one is put down in.
  h.reloadTimer = 0;
  h.ammoTimer = 0;
  h.heatLocked = false;
  if (!instance) {
    h.weapon = null;
    return;
  }
  const def = ctx.weapons.get(instance.defId);
  if (!def) throw new Error(`unknown weapon def: ${instance.defId}`);
  h.weapon = resolveWeapon(def, instance, ctx.modules);
  // Arms `onSwap` triggers — "the incoming weapon fires its first shot at
  // triple power" needs to know a swap happened, and equip is the only place
  // that does.
  noteSwap(state);
}

/** Re-fold after a module change. Free respec means this runs often (§7). */
export function refresh(state: WielderState, hand: Hand, ctx: WielderCtx): void {
  const h = hand === 'main' ? state.main : state.off;
  if (!h.instance) return;
  const def = ctx.weapons.get(h.instance.defId);
  if (def) h.weapon = resolveWeapon(def, h.instance, ctx.modules);
}

// ------------------------------------------------------------------ scratch

const scratchTargets = new Int32Array(64);
const scratchResult = newHitResult();
const scratchHit: HitInput = {
  attacker: 0, target: 0, weapon: null as unknown as ResolvedWeapon, node: null,
  hand: 'main', hx: 0, hy: 0, dirX: 1, dirY: 0,
  stats: defaultStats(), rhythm: 0, charge: 1, backstab: false, continuous: false,
  windows: 0, chargePrimer: 0, echo: false,
};
const scratchHandling: TriggerMods = {
  damageMul: 1, critChanceAdd: 0, critMulAdd: 0, detonationMul: 1,
  primedDamageMul: 1, lifestealFrac: 0, poiseMul: 1, knockbackMul: 1,
};

// --------------------------------------------------------------- main entry

/**
 * Advance both hands one frame. `dt` is seconds; internal timers are ms because
 * every number in the design docs is authored in ms and converting at the
 * boundary is cheaper than converting in the reader's head.
 */
export function updateWielder(
  state: WielderState,
  w: World,
  i: number,
  cells: CellWorld,
  input: Input,
  dt: number,
  ctx: WielderCtx,
): void {
  const ms = dt * 1000;
  state.clock += ms;
  state.pogoed = false;
  state.vaulted = false;
  decayWindows(state, ms);
  // Echoes are owed by a blow that has already landed, so they run ahead of the
  // hitstop gate below: a freeze must not be able to postpone a debt.
  tickEchoes(state, w, i, cells, ms, ctx);

  // Hitstop is drained by `tickCombatTimers` in combat/status.ts — one drain
  // site for every entity, or the freeze runs at N× speed for whoever has N
  // systems decrementing it. This one only observes it.
  if (w.hitstop[i] > 0) {
    decayRhythm(state, dt);
    return;
  }

  // Aim, sampled once per update, before either hand can start a node.
  //
  // Directions aim and buttons fire: holding up, down or a diagonal decides
  // *where* the weapon points, and J / K / L (and the mouse buttons bound to
  // them) still decide *when* it goes off. Nothing about the button half
  // changes, and with no vertical held this is `NaN` — neutral — which means
  // every weapon keeps its authored angle and today's game is the default case.
  //
  // It matters that this is derived from `input.axis` rather than from
  // `held('up' | 'down')`: the axis is where the gamepad stick and the keyboard
  // have already been reconciled, so a stick aims without a second code path.
  state.aim = aimAngleFor(input.axis.x, aimAxisY(input.axis.y, w.grounded[i] === 1));

  // The same axis, read as a clock rather than as a flag — issue #139. Sampled
  // beside the aim and before either hand runs, for the same reason the aim is:
  // both hands must see one answer, and `tryStart` is downstream of it.
  state.downMs = downHeld(input) ? state.downMs + ms : 0;

  // Stance: a tap on the off-hand attacks, a hold remaps the main-hand moveset.
  // The hold has to be sampled *before* either hand runs, because `tryStart`
  // consumes the off-hand press — sampling after meant the 165 ms stance hold
  // could only ever latch once an off-hand attack had already fired.
  state.stance =
    state.off.weapon !== null &&
    input.heldFor('offAttack') * 1000 > STANCE_HOLD_MS &&
    state.off.phase === 'idle';

  updateHand(state, state.main, w, i, cells, input, dt, ctx, 'mainAttack');
  // While stance is held the off hand is a modifier, not a weapon: letting it
  // also swing would mean the sword+shield thrust of §8 always came with a
  // shield bash attached, and the player could never hold the modifier.
  if (!state.stance) {
    updateHand(state, state.off, w, i, cells, input, dt, ctx, 'offAttack');
  } else if (state.off.phase !== 'idle') {
    updateHand(state, state.off, w, i, cells, input, dt, ctx, 'offAttack');
  }

  if (bothHandsIdle(state)) decayRhythm(state, dt);
  if (bothHandsIdle(state)) state.canJumpCancel = false;
}

/**
 * The vertical the aim is allowed to see. **Down aims only in the air.**
 *
 * On the ground `down` already means something: it is `wantsCrouch` in
 * `movement.ts`, it is the heavy entry in `pickEntryKind`, and it is the drop
 * through a one-way in `wantsDrop`. Letting it *also* be an aim is the
 * down-as-selector versus down-as-aim collision #100 spent three options trying
 * to arbitrate — and every one of those options had to key on weapon class or
 * hitbox kind, which is why none of them landed. This dissolves the collision
 * instead: on the ground down is unambiguously crouch, in the air it is
 * unambiguously aim, and nothing has to decide.
 *
 * So a grounded down-attack fires **level, from crouch height**. The height is
 * not arranged here and does not need to be: `updateCrouch` has already lowered
 * `halfH` and dropped `w.y[i]` by the same amount with the feet fixed (#98), and
 * `fireOrSwing` builds its hitbox origin from `w.y[i]`. Holding down therefore
 * moves the swing down the body by 30% of standing half-height without the aim
 * being involved at all — which is the whole point of routing it through the
 * stance rather than through the angle.
 *
 * **The descent verb is not deleted, it is moved.** Digging downward now costs
 * a jump: leave the ground, hold down, attack before landing. `cc2c693` made
 * grounded down-attacks level once before and `6baa75d` reverted it, correctly,
 * because that change removed the verb with nothing in its place. The airborne
 * path is what replaces it, and `tests/aiming.test.ts` measures that it is
 * reachable rather than merely present.
 *
 * **The catalogue already assumed this.** Every node in the game with an
 * authored downward `hitbox.angle` — all four of them, 1.4 to 1.57 — is a
 * `dair`, and a `dair` is airborne by definition. There was never a grounded
 * node authored to point at the floor.
 *
 * Up is untouched: it has no second meaning on the ground.
 */
export function aimAxisY(axisY: number, grounded: boolean): number {
  return grounded && axisY > 0 ? 0 : axisY;
}

function decayWindows(state: WielderState, ms: number): void {
  if (state.killWindow > 0) state.killWindow = Math.max(0, state.killWindow - ms);
  if (state.parryWindow > 0) state.parryWindow = Math.max(0, state.parryWindow - ms);
  if (state.swapWindow > 0) state.swapWindow = Math.max(0, state.swapWindow - ms);
  if (state.parryActive > 0) state.parryActive = Math.max(0, state.parryActive - ms);
}

/** The timed trigger events currently armed, as a `TriggerFlag` mask. */
export function triggerWindows(state: WielderState): TriggerMask {
  let m = 0;
  if (state.killWindow > 0) m |= TriggerFlag.KILL;
  if (state.parryWindow > 0) m |= TriggerFlag.PARRY;
  if (state.swapWindow > 0) m |= TriggerFlag.SWAP;
  return m;
}

function decayRhythm(state: WielderState, dt: number): void {
  state.rhythm = Math.max(0, state.rhythm - RHYTHM_DECAY * dt);
}

/**
 * Nothing is swinging — the condition the rhythm meter drains under.
 *
 * Named because it has two readers and they must not disagree: `updateWielder`
 * uses it to decide whether to run `decayRhythm`, and `rhythmDecaying` below
 * uses it to answer whether the meter *is* draining. That is exactly the shape
 * #133 was — a writer and a reader with private ideas of the same fact — so the
 * two share one spelling rather than two copies that agree today.
 */
function bothHandsIdle(state: WielderState): boolean {
  return state.main.phase === 'idle' && state.off.phase === 'idle';
}

/**
 * Is the meter draining right now? (#133)
 *
 * The HUD's decay tell used to read `rhythm > 0 && lastHand === null`, which
 * `startNode` makes unreachable: `lastHand` follows the button pressed and is
 * never put back to `null`, so after the first swing of a run the tell was
 * permanently off. The flag was a *proxy* for the meter and the proxy was wrong.
 *
 * This is the meter instead, and it is two facts rather than one:
 *
 *   - **the alternation window has lapsed.** Inside `RHYTHM_WINDOW_MS` of the
 *     last swing the chain is still alive — the next hand still counts — and
 *     the small dip between two swings is not something to warn about. A tell
 *     that fired on any dip would strobe through every combo.
 *   - **and the value is falling**, i.e. `decayRhythm` is what is running. A
 *     hand held in a long charge is past the window and losing nothing yet.
 *
 * Exported for the same reason `rhythmRecoveryMul` is: the reader must not
 * transcribe the rule, or the transcript is free to drift from it.
 */
export function rhythmDecaying(state: WielderState): boolean {
  if (state.rhythm <= 0) return false;
  if (!bothHandsIdle(state)) return false;
  const last = Math.max(state.main.lastStart, state.off.lastStart);
  return state.clock - last > RHYTHM_WINDOW_MS;
}

/**
 * Recovery is shortened as the rhythm meter fills — up to 30% off.
 *
 * Exported so a test can read the fraction instead of transcribing it.
 */
export function rhythmRecoveryMul(state: WielderState): number {
  return 1 - state.rhythm * 0.3;
}

// --------------------------------------------------------------- per hand

function updateHand(
  state: WielderState,
  h: HandState,
  w: World,
  i: number,
  cells: CellWorld,
  input: Input,
  dt: number,
  ctx: WielderCtx,
  action: 'mainAttack' | 'offAttack',
): void {
  const wp = h.weapon;
  if (!wp) return;
  const ms = dt * 1000;
  h.sinceEnd += ms;
  if (h.contactTimer > 0) h.contactTimer = Math.max(0, h.contactTimer - ms);
  if (h.sinceEnd > CHAIN_WINDOW_MS) h.chainFrom = null;

  // Attacks drag horizontal velocity. Never a stop — momentum is the pillar.
  if (h.phase === 'windup' || h.phase === 'active') {
    const drag = attackDrag(wp.weight);
    w.vx[i] *= Math.pow(drag, dt * 60);
  }

  tickResources(h, w, i, dt);

  switch (h.phase) {
    case 'idle':
      // A dry hand reloads itself rather than sitting there clicking, because
      // a weapon the player has to un-jam is inventory friction and docs/04 §7
      // rules that out by name.
      //
      // The order is load-bearing and it used to be wrong: `heatLocked` was
      // checked *with* `outOfFuel`, above the reload, so a hand that was both
      // empty and overheated started neither timer — it waited out the heat in
      // silence and only then began to reload. `tickResources` cools an idle
      // hand as well as a reloading one, so this was never a deadlock, but it
      // serialised two waits the design explicitly wants overlapped: the
      // comment there says the reload window *is* the cooldown window, one
      // pause paying for both. What the player saw was a weapon that was
      // empty, quiet, and not visibly reloading.
      //
      // So heat now gates only *acting*. `outOfFuel` stays above the reload
      // because it is a different resource — a fuel weapon has no magazine, so
      // `needsReload` is false for it regardless, and the check is about a
      // charge that has to refill before the hand can do anything at all.
      //
      // No shipping weapon can currently reach the empty-and-overheated state:
      // #43 capped every heat-bearing magazine at 0.90 of the heat bar. A
      // module that adds ammo or raises heat, or docs/04 §11's melee ammo
      // refund, can.
      if (outOfFuel(h)) break;
      if (needsReload(h)) {
        beginReload(h);
        break;
      }
      if (h.heatLocked) break;
      tryStart(state, h, w, i, input, ctx, action);
      break;

    case 'reloading':
      h.reloadTimer -= ms;
      if (h.reloadTimer <= 0) {
        h.instance!.ammo = magazineOf(wp.def);
        h.phase = 'idle';
      }
      break;

    case 'charging': {
      h.chargeHeld += ms;
      if (!input.held(action)) releaseCharge(state, h, w, i, ctx);
      break;
    }

    case 'parry': {
      h.timer += ms;
      // The window is the parry; what follows is the recovery you eat for
      // guessing wrong. `parryActive` is drained centrally so an incoming hit
      // resolved by any system sees the same number.
      if (h.timer >= wp.parryWindowMs + parryRecoveryMs(wp)) cancelNode(h, w, i);
      break;
    }

    case 'windup':
      h.timer += ms;
      if (h.timer >= nodeWindup(h)) {
        h.timer = 0;
        h.phase = 'active';
        applyImpulse(h, w, i);
        fireOrSwing(state, h, w, i, cells, ctx, true);
      }
      break;

    case 'active':
      h.timer += ms;
      fireOrSwing(state, h, w, i, cells, ctx, false);
      if (h.timer >= nodeActive(h)) {
        h.timer = 0;
        h.phase = 'recovery';
      }
      break;

    case 'recovery': {
      h.timer += ms;
      const rec = nodeRecovery(state, h);
      // Cancel windows: dash always after `cancelAfter`, jump only on confirm.
      const past = h.timer / Math.max(1, rec);
      if (past >= h.node!.cancelWindow[0] && input.consume('dash', DASH_BUFFER_MS)) {
        finishNode(h, w, i);
        fireTriggers(state, h, 'onDash');
        break;
      }
      // A buffered attack inside the cancel window chains straight through.
      if (past >= h.node!.cancelWindow[0] && tryChain(state, h, w, i, input, ctx, action)) break;
      if (h.timer >= rec) finishNode(h, w, i);
      break;
    }

    default:
      break;
  }

  restoreGravity(h, w, i);
}

/**
 * The three node timings the hand counts down, in real milliseconds.
 *
 * All three are `nodeTiming` (weaponData.ts) — one implementation, shared with
 * `resolve.ts` and read directly by `tests/balance.test.ts`. They are exported
 * because they were not: the balance suite used to transcribe this arithmetic
 * into a private mirror, which is a test reading a *copy* of a number and is
 * the same defect as `combat.test.ts:1189` reading the dead `cadence` field.
 * The precedent is `bodyQuad` (Phase 2): if a test has to know a number, the
 * runtime exports the function that produces it.
 *
 * `cadenceScale` is the whole of what the authored `cadenceMulBase` does; see
 * weaponData.ts. There is no longer a global `SWING_SPEED` divisor here — the
 * 29 timing triples in weaponData are authored at the speed they actually run
 * at, so raw node ms are true ms.
 */
export function nodeActive(h: HandState): number {
  return nodeTiming(h.weapon!, h.node!).active;
}

export function nodeWindup(h: HandState): number {
  return nodeTiming(h.weapon!, h.node!).windup;
}

export function nodeRecovery(state: WielderState, h: HandState): number {
  const wp = h.weapon!;
  // Trigger modules that shorten recovery (`onKill recoveryMul: 0.7`) are a
  // handling payload, so they apply here rather than in the hit path — and so
  // they are outside `nodeTiming`, which the hitstop path also reads.
  let rec = nodeTiming(wp, h.node!, rhythmRecoveryMul(state)).recovery;
  const armed = triggerWindows(state);
  if ((wp.triggerMask & armed) !== 0) {
    for (const t of wp.triggers) {
      if ((t.mask & armed) === 0) continue;
      if (t.effect.recoveryMul !== undefined) rec *= t.effect.recoveryMul;
    }
  }
  return Math.max(1, rec);
}

// --------------------------------------------------------------- resources

/**
 * Advance ammo, heat and charge for one hand.
 *
 * Every one of these is a rate against wall time rather than against frames,
 * for the same reason `CONTINUOUS_HIT_MS` is: a drill that burns fuel per
 * substep empties four times faster at 120 Hz than at 30.
 */
function tickResources(h: HandState, w: World, i: number, dt: number): void {
  const wp = h.weapon!;
  const inst = h.instance;
  if (!inst) return;
  /**
   * Recovery is not rest.
   *
   * Crediting cooling and refuelling during recovery makes a tap-grind — which
   * is how a drill is actually held, since starting a node needs a fresh
   * buffered press — net *negative* on heat: the hand spends more of a grind
   * recovering than cutting. The drill would then never reach the overheat its
   * own blurb promises. Only a hand that has genuinely stopped recovers.
   */
  const resting = h.phase === 'idle' || h.phase === 'reloading';

  if (h.phase === 'active' && wp.def.heatPerSecond) {
    addHeat(h, wp.def.heatPerSecond * dt, w, i);
  } else if (resting && inst.heat > 0) {
    // The reload window is also the cooldown window. That is deliberate: it
    // means one pause pays for both, instead of the player having to learn two
    // different timers for the same weapon.
    inst.heat = Math.max(0, inst.heat - HEAT_COOL_PER_SEC * dt);
  }
  if (h.heatLocked && inst.heat <= HEAT_CLEAR) h.heatLocked = false;

  // A continuous weapon has no trigger pull to bill, so its magazine is spent
  // as tenths of a second of contact — the beam's 60 rounds are 6 seconds.
  if (h.phase === 'active' && isContinuous(wp.def) && magazineOf(wp.def) > 0) {
    h.ammoTimer += dt * 1000;
    while (h.ammoTimer >= CONTINUOUS_HIT_MS && inst.ammo > 0) {
      h.ammoTimer -= CONTINUOUS_HIT_MS;
      inst.ammo--;
    }
    if (inst.ammo <= 0) cancelNode(h, w, i);
  }

  switch (chargeKindOf(wp.def)) {
    case 'fuel':
      // docs/04 §3.1: the drill "consumes fuel". It never did.
      if (h.phase === 'active') {
        inst.charge = Math.max(0, inst.charge - FUEL_DRAIN_PER_SEC * dt);
        if (inst.charge <= 0) cancelNode(h, w, i);
      } else if (resting) {
        inst.charge = Math.min(1, inst.charge + FUEL_REGEN_PER_SEC * dt);
      }
      break;

    case 'hunger':
      // "Feed it kills or it feeds on you." The first half is `feedHunger`; this
      // is the second, and neither existed.
      inst.charge = clamp(inst.charge + HUNGER_PER_SEC * dt, 0, 1);
      if (inst.charge >= 1) w.hp[i] -= HUNGER_BITE_DPS * dt;
      break;

    case 'guard':
      // Only a shield that is *down* recovers. `updateHand` is not even called
      // for the off hand while the modifier is held, so this is reached exactly
      // when the guard is lowered — which is the whole trade: holding block is
      // not free, and the way you pay is by not holding it.
      if (resting) inst.charge = Math.min(1, inst.charge + GUARD_REGEN_PER_SEC * dt);
      break;

    case 'spin':
      if (h.phase === 'charging') {
        const top = topChargeMs(wp.def);
        if (top > 0) inst.charge = Math.min(1, h.chargeHeld / top);
      } else if (h.phase === 'idle') {
        inst.charge = Math.max(0, inst.charge - SPIN_DECAY_PER_SEC * dt);
      }
      break;

    default:
      break;
  }
}

function topChargeMs(def: WeaponDef): number {
  const t = def.chargeStates;
  return t && t.length > 0 ? t[t.length - 1].holdMs : 0;
}

/** Hold time a fresh press starts at, so retained spin is not thrown away. */
function retainedSpinMs(h: HandState): number {
  const def = h.weapon!.def;
  if (chargeKindOf(def) !== 'spin') return 0;
  return Math.max(0, h.instance!.charge) * topChargeMs(def);
}

function needsReload(h: HandState): boolean {
  return magazineOf(h.weapon!.def) > 0 && h.instance!.ammo <= 0;
}

function beginReload(h: HandState): void {
  h.phase = 'reloading';
  h.reloadTimer = h.weapon!.def.reloadMs ?? 1000;
  h.ammoTimer = 0;
}

function outOfFuel(h: HandState): boolean {
  return chargeKindOf(h.weapon!.def) === 'fuel' && h.instance!.charge < FUEL_RESTART;
}

/**
 * Bill one trigger pull. The magazine floors at zero rather than refusing a
 * burst it cannot fully cover: the last round of a magazine still fires the
 * launcher's three-shell heavy, it just does not leave you anything after.
 */
function spendAmmo(h: HandState, node: ComboNode): void {
  const def = h.weapon!.def;
  const cost = ammoCostOf(def, node);
  if (cost <= 0) return;
  h.instance!.ammo = Math.max(0, h.instance!.ammo - cost);
}

function addHeat(h: HandState, amount: number, w: World, i: number): void {
  if (amount <= 0) return;
  const inst = h.instance!;
  inst.heat = Math.min(1, inst.heat + amount);
  if (inst.heat < 1 || h.heatLocked) return;
  h.heatLocked = true;
  // OVERHEATED is a 6 dps burn in the status table, so "will cook you if you
  // never let go" — the Deepcut Auger's own blurb — is finally true.
  applyStatus(w, i, StatusKind.OVERHEATED, 3, 1);
  cancelNode(h, w, i);
}

/**
 * A kill feeds every living weapon the wielder is holding.
 *
 * Both hands, not just the one that landed the blow: the symbiote is attached
 * to the player, and "it is hungry" is a fact about the run rather than about
 * which hand happened to finish something.
 */
function feedHunger(state: WielderState, amount: number): void {
  for (const h of [state.main, state.off]) {
    if (!h.instance || !h.weapon) continue;
    if (chargeKindOf(h.weapon.def) !== 'hunger') continue;
    h.instance.charge = clamp(h.instance.charge - amount, 0, 1);
  }
}

/**
 * docs/04 §8: "melee refreshes projectile ammo on hit. The most fluid pairing."
 *
 * This is the whole reason the resource layer is not just a tax. Ammo makes
 * range cost something; this makes closing the distance the way you pay for it,
 * so a melee main hand and a projectile off hand becomes "spend the magazine,
 * then close" rather than two independent buttons.
 */
function refillOtherHand(state: WielderState, h: HandState): void {
  if (h.weapon!.def.archetype !== 'melee') return;
  const other = h.hand === 'main' ? state.off : state.main;
  const def = other.weapon?.def;
  if (!def || !other.instance) return;
  const mag = magazineOf(def);
  if (mag <= 0 || other.instance.ammo >= mag) return;
  other.instance.ammo = Math.min(mag, other.instance.ammo + AMMO_PER_MELEE_HIT);
}

/**
 * Bank charge from a connected melee hit.
 *
 * `ModuleEffect.melee.chargePerHit` is authored on three modules, folded by
 * `resolve.ts` into `ResolvedWeapon.melee.chargePerHit`, and until now read by
 * nobody — the other half of issue #15. A hunger weapon reads a hit as a
 * feeding, which is why the sign flips there.
 */
function bankCharge(h: HandState): void {
  const per = h.weapon!.melee.chargePerHit;
  if (per <= 0 || !h.instance) return;
  const kind = chargeKindOf(h.weapon!.def);
  const delta = kind === 'hunger' ? -per : per;
  h.instance.charge = clamp(h.instance.charge + delta, 0, 1);
}

// ------------------------------------------------------------- transitions

function tryStart(
  state: WielderState,
  h: HandState,
  w: World,
  i: number,
  input: Input,
  ctx: WielderCtx,
  action: 'mainAttack' | 'offAttack',
): void {
  // Never poll held state to *start* an attack: buffered presses are what make
  // an input feel honoured rather than sampled.
  if (!input.consume(action, ATTACK_BUFFER_MS)) return;

  const wp = h.weapon!;
  const kind = pickEntryKind(state, h, w, i, input);

  // Charge weapons open into a hold rather than committing immediately — but
  // only for the grounded, committed openers. A dash-attack, an air-attack and
  // a down-air pogo are context moves with no charge tier authored anywhere,
  // and routing them into the hold is what stripped 4 of 29 classes of all
  // three (docs/04 §4: "dash-attack is a distinct node, always"; "down-air
  // always pogos").
  const chargeable =
    (wp.def.chargeStates?.length ?? 0) > 0 && (kind === 'light' || kind === 'heavy');
  if (chargeable && input.held(action)) {
    h.phase = 'charging';
    // A chain weapon resumes its spin rather than starting from a dead stop.
    h.chargeHeld = retainedSpinMs(h);
    h.chargeMul = 1;
    h.chargeRadiusMul = 1;
    h.chargePrimer = 0;
    h.chargeEntry = kind;
    return;
  }
  startNode(state, h, w, i, entryNode(state, h, kind, ctx), ctx);
}

function tryChain(
  state: WielderState,
  h: HandState,
  w: World,
  i: number,
  input: Input,
  ctx: WielderCtx,
  action: 'mainAttack' | 'offAttack',
): boolean {
  if (!input.consume(action, ATTACK_BUFFER_MS)) return false;
  const from = h.node!;
  const grounded = w.grounded[i] === 1;
  // The same rule the entry uses, and reached through the same predicate: a
  // chain that continued into `onHeavy` for as long as the crouch was held
  // would leave the light chain unreachable from a crouch even once the entry
  // stopped being a heavy (#139).
  const heavy = downHeld(input) && grounded && downIsModifier(state);
  const nextId = heavy ? from.next.onHeavy : !grounded ? from.next.onAir : undefined;
  const node = nextId
    ? h.weapon!.nodes.get(nextId)
    : (!heavy && grounded ? nextLightAfter(h, from) ?? undefined : undefined);
  startNode(
    state, h, w, i,
    node ?? entryNode(state, h, pickEntryKind(state, h, w, i, input), ctx),
    ctx,
  );
  return true;
}

/**
 * "Is down held?" — read from the axis, never from the action — issue #96.
 *
 * The two are not equivalent and the difference is a live defect. `pollGamepad`
 * writes the left stick's deflection into `input.axis` but never calls
 * `press`/`release` for the directional actions; only the keyboard and the
 * d-pad do. So `held('down')` is false on a stick-only pad no matter how far
 * the stick is pushed, and every consumer written against it was unreachable
 * there — which was these three call sites: the grounded heavy entry and every
 * `onHeavy` combo branch, i.e. 29 weapons and all 203 nodes.
 *
 * What made it hard to see is that the *aim* worked. `aimAngleFor` reads the
 * axis, so on a controller the character pointed down and the attack that
 * choice selects did not change — which reads as an unresponsive game rather
 * than as a missing binding.
 *
 * The rule, stated the way #78 and #82 state theirs: **the axis is what "down"
 * means.** `held('down')` remains a keyboard and d-pad fact and is fine for
 * anything that wants a discrete key; nothing that asks a *directional*
 * question may use it. The threshold is shared with `aimAngleFor` and with
 * `movement.ts`'s `DOWN_INTENT`, so a stick position that aims down is exactly
 * one that selects a heavy — the two can no longer disagree.
 *
 * The alternative — synthesising presses in `pollGamepad` — was rejected as
 * the wider change: it touches every `held('left'/'right')` consumer including
 * the menu pump and the prologue skip, and it has to avoid feeding back into
 * `update`'s own axis reconciliation.
 */
export const DOWN_HELD = 0.5;

export function downHeld(input: Input): boolean {
  return input.axis.y > DOWN_HELD;
}

/**
 * How long a held `down` stays an *attack modifier* before it becomes a
 * *stance*, in ms — issue #139.
 *
 * ---------------------------------------------------------------------------
 * THE DEFECT
 * ---------------------------------------------------------------------------
 *
 * Crouching is holding down, and holding down selected the heavy. So every
 * attack thrown from a crouch was a heavy, and **there was no way to throw a
 * light attack while crouched at all.** With the shipped off-hand Repeater,
 * whose heavy is an authored two-round burst, that is the "crouching fires
 * twice" report: #137 was right that nothing doubled and that the burst had
 * merely become visible, and it stopped one question short of this one.
 *
 * ---------------------------------------------------------------------------
 * THE RULE: A MODIFIER IS AN EDGE, A STANCE IS A LEVEL
 * ---------------------------------------------------------------------------
 *
 * `down` was being read as a level by two owners at once, and a level cannot be
 * shared: whatever the second owner reads, it reads for as long as the first
 * one holds. So one of the two readings becomes an edge, and it has to be the
 * attack modifier, because the crouch is the reading that is *inherently* a
 * level — `movement.ts` lowers `halfH` and holds it lowered, and 52% of the
 * crouch-only runs in the world are travelled through, so the crouch is held
 * for seconds at a time by design (#98).
 *
 * **Tap down and attack — the two arriving together — throws the heavy. Attack
 * from a crouch you are already holding throws the light.** The heavy keeps
 * exactly the input #100 established as the only one that reaches it; what it
 * loses is the tail of that input, which belonged to the stance.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT THE OTHER TWO OPTIONS
 * ---------------------------------------------------------------------------
 *
 * **"Crouch suppresses the heavy, the way `aimAxisY` suppresses the down aim."**
 * `aimAxisY` can do that because the down *aim* has somewhere to go: it is not
 * deleted, it moves to the air, and `tests/aiming.test.ts` measures that it is
 * reachable there. The heavy has nowhere: `pickEntryKind` only reaches the
 * heavy line while grounded, airborne-down is already `dair`, and suppressing
 * it grounded would take one node per weapon (30) and every `onHeavy` branch
 * (210 of 210) out of the game — the exact cost `tests/downOwnership.test.ts`
 * exists to state.
 *
 * **"The heavy moves to a distinct input."** There is no input to move it to.
 * `dash` + attack is the dash entry, a held `offAttack` is the stance modifier,
 * `parry` and `ability` are their own verbs, and the pad map is *full*: sixteen
 * buttons, seventeen actions, with `codex` already left off
 * (`DEFAULT_PAD_BINDINGS`). A new `heavy` action would be reachable on a
 * keyboard and unreachable on a controller, which is #96 verbatim.
 *
 * ---------------------------------------------------------------------------
 * THE NUMBER
 * ---------------------------------------------------------------------------
 *
 * 220 ms, and it is not a fresh guess: it is the window `pickEntryKind` already
 * uses one line above for the dash entry (`input.buffered('dash', 220)`) — this
 * file's existing answer to "how recent must a modifier be to colour the next
 * attack". Two windows for one question would be two numbers to tune. It is
 * measured against the *press*, not against the attack, so it is not a timing
 * test: down and attack pressed together are ~8 ms apart at 120 Hz, and a
 * player who means the heavy has thirteen substeps of slack.
 *
 * Down held *airborne* accumulates too (see `WielderState.downMs`). Holding
 * down through a jump and attacking on landing is therefore a light, which is
 * the same sentence as everywhere else: you are crouched, so you are not
 * throwing the modifier.
 */
export const HEAVY_WINDOW_MS = 220;

/**
 * Is the held `down` still an attack modifier, or has it become the crouch?
 *
 * Exported so the rule can be pinned as a function rather than as a source
 * scan, the way `downHeld` and `wantsDrop` are.
 */
export function downIsModifier(state: WielderState): boolean {
  return state.downMs <= HEAVY_WINDOW_MS;
}

export type EntryKind = 'light' | 'heavy' | 'dash' | 'air' | 'dair';

/**
 * Which entry class a fresh press opens with. The distinct dash / air /
 * down-air nodes of docs/04 §4 are selected by context, not by a separate
 * button. Split from node selection so the charge path can remember the
 * *class* across a hold rather than collapsing it to light-or-heavy.
 */
function pickEntryKind(
  state: WielderState,
  h: HandState,
  w: World,
  i: number,
  input: Input,
): EntryKind {
  const grounded = w.grounded[i] === 1;
  if (!grounded) return downHeld(input) ? 'dair' : 'air';
  if (input.buffered('dash', 220) || Math.abs(w.vx[i]) > 200) return 'dash';
  // Down *and still an edge* — `HEAVY_WINDOW_MS`. Past the window the player is
  // crouching, and a crouched press is an ordinary light thrown from crouch
  // height, which `aimAxisY` has already made fire level.
  if (downHeld(input) && downIsModifier(state)) return 'heavy';
  if (h.hand === 'main' && state.stance) return 'dash';
  return 'light';
}

function entryNode(
  state: WielderState,
  h: HandState,
  kind: EntryKind,
  ctx: WielderCtx,
): ComboNode {
  const wp = h.weapon!;
  if (kind === 'dash' && h.hand === 'main' && state.stance) {
    const n = stanceNode(state, wp);
    if (n) return n;
  }
  // Place selects a node before anything else does — the relic blade's whole
  // identity is that the button means something different here than it did a
  // band ago.
  const attuned = attunedNode(h, kind, ctx);
  if (kind !== 'light') return attuned ?? wp.entries[kind];

  // Chain continuation if the previous node is still inside its window.
  if (h.chainFrom && h.sinceEnd <= CHAIN_WINDOW_MS) {
    const n = nextLightAfter(h, h.chainFrom);
    if (n) return n;
  }
  // A fresh chain: the counter that `extraChainHits` spends starts over.
  h.chainExtra = 0;
  return attuned ?? mutatedOpener(h) ?? wp.entries.light;
}

/**
 * The next node in the light chain, honouring `melee.extraChainHits`.
 *
 * The chain is authored as a loop — l1 → l2 → l3 → l1 — and the field means
 * "extra hits appended to the light chain", so the extension is the last node
 * repeating before the loop closes rather than a new node nobody authored. It
 * had no reader in the runtime *and* no author in the catalogue, which is the
 * other half of issue #15; the Featherweight Frame now carries it.
 *
 * Shared by the buffered-press path and the mid-recovery chain path, because a
 * chain that lengthens only when you cancel into it is not a longer chain.
 */
function nextLightAfter(h: HandState, from: ComboNode): ComboNode | null {
  const wp = h.weapon!;
  const id = from.next.onLight;
  const next = id ? wp.nodes.get(id) : undefined;
  if (!next) return null;
  if (next !== wp.entries.light) return next;
  if (h.chainExtra < wp.melee.extraChainHits) {
    h.chainExtra++;
    return from;
  }
  h.chainExtra = 0;
  return next;
}

/**
 * The living weapon's signature: hunger does not merely scale the damage
 * number, it decides which node the light button selects.
 *
 * Below the threshold the chain runs up from the opener as every other class's
 * does. Above it the symbiote opens with the wide finisher and works *down* —
 * the big swing comes first, and what follows it is the weak end of the chain,
 * so a starving weapon is not simply a stronger one. That is the "mutates its
 * own moveset" half of docs/04 §3.1, and no other class lets a resource choose
 * a node.
 */
function mutatedOpener(h: HandState): ComboNode | null {
  const wp = h.weapon!;
  if (chargeKindOf(wp.def) !== 'hunger') return null;
  if ((h.instance?.charge ?? 0) < MUTATE_HUNGER) return null;
  return wp.nodes.get(`${wp.def.id}:l3`) ?? null;
}

/**
 * The relic blade's signature: *where you are* selects which node the buttons
 * open on (docs/04 §3.1).
 *
 * This waited on worldgen and the wait was the point. The spine is now eight
 * ordered bands (docs/05 §1, `world/biomes.ts` MAIN_PATH), so a biome is a
 * position on a ramp rather than a flavour of room, and "the blade rewrites its
 * moveset when the biome changes" finally has something to mean: descending is
 * the input. Consecutive bands are deliberately never assigned the same node —
 * a band that handed back the weapon you already had would be a checkpoint you
 * walk through, which is exactly the reason four biomes are wings and not bands.
 *
 * The four side-content biomes get attunements too. They are not lesser content
 * (`world/biomes.ts` SIDE_CONTENT) and a relic blade that went inert in a wing
 * would say they were.
 *
 * Keyed on plain strings. `unlockEffects.ts` keeps meta from importing combat
 * for the same reason this keeps combat from importing the world's taxonomy;
 * `tests/combat.test.ts` asserts the table covers `BIOME_IDS` so the decoupling
 * cannot rot silently.
 */
/** Node suffixes, as `weaponData.ts` spells them — the heavy is `hv`. */
export type RelicNode = 'l1' | 'l2' | 'l3' | 'hv';

export const RELIC_ATTUNEMENT: Readonly<Record<string, RelicNode>> = {
  // MAIN_PATH order, top to bottom. Read down the column: no repeat in a row.
  rotwood_reach: 'l1',
  sunken_vault: 'l2',
  emberworks: 'hv',
  cinder_march: 'l3',
  cathedral_of_salt: 'l1',
  verdigris_spire: 'hv',
  nanite_reef: 'l2',
  hollow_choir: 'l3',
  // The surface, above MAIN_PATH[0]: the blade wakes there the way it wakes
  // at the top of the descent, so the walk to the mouth and the first band
  // are one attunement — the continuity the surface keeps now that its
  // palette is its own (docs/27 §12 items 4 and 5).
  surface: 'l1',
  // The wings.
  chlorine_fen: 'hv',
  meatworks: 'l3',
  coldforge: 'l2',
  the_long_hour: 'l1',
};

/** The node a fresh light press opens on, and the node the heavy gives up. */
const RELIC_DEFAULT_LIGHT: RelicNode = 'l1';

/**
 * Which node the relic blade's `light` or `heavy` entry resolves to here.
 *
 * It is a **swap**, never a gain: when a biome moves the wall-cutting heavy onto
 * the light button, the heavy button inherits the light opener in exchange. A
 * biome that handed over the strong node for free would be the "strictly better,
 * no trade" shape docs/04 §9 forbids, and it would make the descent a list of
 * upgrades rather than a list of different weapons.
 *
 * Returns null for every other class and for an unrecognised biome — rule three
 * of `unlockEffects.ts` applied here: content drift costs the player a *variant*,
 * not the run.
 */
function attunedNode(h: HandState, kind: EntryKind, ctx: WielderCtx): ComboNode | null {
  const wp = h.weapon!;
  if (wp.def.class !== 'relicBlade') return null;
  if (kind !== 'light' && kind !== 'heavy') return null;
  const biome = ctx.biomeId?.();
  if (!biome) return null;
  const attuned = RELIC_ATTUNEMENT[biome];
  if (!attuned) return null;
  const want =
    kind === 'light'
      ? attuned
      // The heavy button only changes when the light took its node.
      : attuned === 'hv' ? RELIC_DEFAULT_LIGHT : null;
  if (want === null) return null;
  return wp.nodes.get(`${wp.def.id}:${want}`) ?? null;
}

/**
 * Stance consults the *off* hand — that is the entire mechanic of docs/04 §8.
 * The previous implementation returned the main hand's own dash node, which
 * means the moveset was identical whatever was in the other hand, and
 * "sword + shield gains a thrust; sword + dagger gains a cross-slash" described
 * nothing that existed.
 */
function stanceNode(state: WielderState, wp: ResolvedWeapon): ComboNode | null {
  const off = state.off.weapon;
  if (!off) return null;
  switch (off.def.class) {
    // A braced off-hand frees the main hand to commit forward: the thrust.
    case 'shield':
    case 'spear':
    case 'lance':
    case 'gauntlet':
      return wp.entries.dash;
    // A second edge crosses the first: the third light, which is every
    // weapon's wide finisher in the skeleton.
    case 'dagger':
    case 'claw':
    case 'whip':
    case 'axe':
      return wp.nodes.get(`${wp.def.id}:l3`) ?? wp.entries.dash;
    // Setup tools buff rather than extend, so the main hand gets its heavy.
    case 'staff':
    case 'sprayer':
    case 'drone':
    case 'bio':
      return wp.entries.heavy;
    default:
      return wp.entries.dash;
  }
}

function releaseCharge(
  state: WielderState,
  h: HandState,
  w: World,
  i: number,
  ctx: WielderCtx,
): void {
  const wp = h.weapon!;
  const tiers = wp.def.chargeStates ?? [];
  // An untiered release is the ordinary attack for the class the press opened
  // with, not a mandatory light. Holding the button for 40 ms should not delete
  // your dash-attack.
  let node: ComboNode = wp.entries[h.chargeEntry];
  h.chargeMul = 1;
  h.chargeRadiusMul = 1;
  h.chargePrimer = 0;
  for (const t of tiers) {
    if (h.chargeHeld < t.holdMs) continue;
    h.chargeMul = t.damageMul;
    // `radiusMul` and `primerApplied` had no reader anywhere, which is why the
    // chain's "radius grows with hold" (docs/04 §3.1) was invisible and its
    // spin-2 stagger primer never landed.
    h.chargeRadiusMul = t.radiusMul;
    if (t.primerApplied !== undefined) h.chargePrimer = t.primerApplied;
    const rn = t.releaseNode ? wp.nodes.get(t.releaseNode) : undefined;
    node = rn ?? wp.entries.heavy;
  }
  // The release spends the spin, but not all of it — see `SPIN_RETAIN`.
  if (h.instance && chargeKindOf(wp.def) === 'spin') {
    const top = topChargeMs(wp.def);
    h.instance.charge = top > 0 ? Math.min(1, h.chargeHeld / top) * SPIN_RETAIN : 0;
  }
  startNode(state, h, w, i, node, ctx);
}

// ------------------------------------------------------------------- parry

/** Post-window recovery for a parry attempt, ms. */
function parryRecoveryMs(wp: ResolvedWeapon): number {
  return wp.def.parry?.recoveryMs ?? 0;
}

/** True if a parry-capable weapon is in either hand. */
export function canParry(state: WielderState): boolean {
  return (state.main.weapon?.parryWindowMs ?? 0) > 0 ||
    (state.off.weapon?.parryWindowMs ?? 0) > 0;
}

/**
 * Open a parry window. `WeaponDef.parry` is authored on sword, gauntlet and
 * shield and had zero readers: the gauntlet's stated identity is "parry into
 * counter is the intended loop", and neither half of that loop existed.
 *
 * Prefers the off hand, because a shield is an off-hand-first design and the
 * player expects the thing they are holding up to be the thing that blocks.
 */
export function startParry(state: WielderState): boolean {
  const pick =
    (state.off.weapon?.parryWindowMs ?? 0) > 0 && state.off.phase === 'idle'
      ? state.off
      : (state.main.weapon?.parryWindowMs ?? 0) > 0 && state.main.phase === 'idle'
        ? state.main
        : null;
  if (!pick) return false;
  pick.phase = 'parry';
  pick.timer = 0;
  // A parry is a thrown verb and not a wait (docs/04 §4), so it announces like
  // one. The same latch `startNode` raises, for the same reason: `startParry`
  // happens to be called from `stepMovement` today, ahead of the poll, and that
  // is an accident of ordering rather than a property of a parry.
  pick.swingPending = true;
  state.parryActive = pick.weapon!.parryWindowMs;
  state.parryHand = pick.hand;
  return true;
}

/**
 * What a defence did to an incoming blow.
 *
 * The old `tryParry` answered only true/false, and a shield eating a hit took
 * the same `true` path as a 220 ms counter — so presentation could not tell a
 * **block** from a **parry**, and gave the guard the counter's flash and its
 * sting. Parry, block and bash are three different things per docs/04 §3.1, and
 * the caller has to be able to say which one happened.
 */
export type BlowOutcome = 'landed' | 'parried' | 'blocked';

/**
 * Resolve an incoming blow against an open parry or a raised guard. Anything
 * but `'landed'` means the caller must not apply the blow.
 *
 * The counter node starts immediately: "parry into counter" is one input, not
 * two, or the window is too tight to ever cash in.
 */
export function resolveIncomingBlow(
  state: WielderState,
  w: World,
  i: number,
  attacker: number,
  ctx: WielderCtx,
): BlowOutcome {
  // No window open is not the end of the question: a shield held up blocks
  // without one. Both live behind this call because it is already the only
  // place an incoming blow asks combat whether it may land.
  if (state.parryActive <= 0) return tryBlock(state, w, i, attacker) ? 'blocked' : 'landed';
  return tryParryWindow(state, w, i, attacker, ctx) ? 'parried' : 'landed';
}

function tryParryWindow(
  state: WielderState,
  w: World,
  i: number,
  attacker: number,
  ctx: WielderCtx,
): boolean {
  const h = state.parryHand === 'off' ? state.off : state.main;
  const wp = h.weapon;
  const def = wp?.def.parry;
  if (!wp || !def) return false;

  state.parryActive = 0;
  state.parryWindow = TRIGGER_WINDOW_MS;
  w.poise[i] = Math.min(w.maxPoise[i], w.poise[i] + def.poiseGain);
  // A parried attacker is stamped with the parry's primer, which is how the
  // gauntlet's loop feeds its own detonation (docs/04 §1).
  if (def.primerApplied !== undefined && attacker >= 0 && w.alive[attacker] === 1) {
    applyStatus(w, attacker, def.primerApplied, 3, 1);
  }
  const counter = def.counterNode ? wp.nodes.get(def.counterNode) : undefined;
  if (counter) startNode(state, h, w, i, counter, ctx);
  else cancelNode(h, w, i);
  return true;
}

/**
 * The shield's signature: block is a *held* state with a resource behind it.
 *
 * Before this the shield was a strictly worse dagger — same code path, less
 * damage, no backstab — with three advertised abilities ("blocks, parries,
 * bashes") of which only the parry, which the sword and gauntlet also have,
 * existed. The distinction that makes it a class:
 *
 *   - a **parry** is a 220 ms window you throw out and either win or eat;
 *   - a **block** is held for as long as the guard lasts, negates the blow
 *     outright, and costs `GUARD_PER_BLOCK` of a bar that only refills once the
 *     shield comes down;
 *   - and a **bash** is the heavy node, which is what the guard is protecting
 *     the space for.
 *
 * The guard is raised by the off-hand modifier — the same hold that puts the
 * main hand in stance (§8) — so bracing and blocking are one input rather than
 * a second button that has to be taught.
 */
function isGuarding(state: WielderState): boolean {
  const h = state.off;
  return state.stance &&
    h.weapon !== null &&
    h.instance !== null &&
    chargeKindOf(h.weapon.def) === 'guard';
}

function tryBlock(state: WielderState, w: World, i: number, attacker: number): boolean {
  if (!isGuarding(state)) return false;
  // A shield covers the side it is held on. Without this, holding the modifier
  // would be unconditionally correct — a defence with no angle to get around is
  // the same "strictly better, no trade" shape §9 forbids, and it would also
  // delete every reason to ever turn around.
  if (attacker >= 0 && Math.sign(w.x[attacker] - w.x[i]) === -w.facing[i]) return false;
  const h = state.off;
  const inst = h.instance!;
  if (inst.charge < GUARD_PER_BLOCK) {
    // Guard break. The blow that breaks it lands, and the wielder eats a
    // stagger on top — a shield that merely stopped blocking would make holding
    // it permanently correct, which is the same "no downside" shape §9 forbids.
    inst.charge = 0;
    applyStatus(w, i, StatusKind.STAGGERED, GUARD_BREAK_STAGGER, 1);
    w.stagger[i] = Math.max(w.stagger[i], GUARD_BREAK_STAGGER);
    return false;
  }
  inst.charge -= GUARD_PER_BLOCK;
  // Blocking is what a shield's poise is for: absorbing a blow braced costs
  // stamina, not balance.
  const gain = h.weapon!.def.parry?.poiseGain ?? 0;
  if (gain > 0) w.poise[i] = Math.min(w.maxPoise[i], w.poise[i] + gain * 0.25);
  return true;
}

/** Latched by the loadout layer on a weapon swap, arming `onSwap` triggers. */
export function noteSwap(state: WielderState): void {
  state.swapWindow = TRIGGER_WINDOW_MS;
}

function startNode(
  state: WielderState,
  h: HandState,
  w: World,
  i: number,
  node: ComboNode,
  ctx: WielderCtx,
): void {
  h.node = node;
  h.phase = 'windup';
  // A swing has begun, and it stays begun until somebody draws it — #142. This
  // is the whole fix: the announcement used to be re-derived from `phase` and
  // `timer` by a poll that runs later in the frame, so a node started from
  // anywhere but inside `updateWielder` was over the edge before the poll came
  // round. Raised here because `startNode` is the one place a node begins, and
  // so it cannot disagree with itself about what a start is.
  h.swingPending = true;
  // The swing commits to the direction it was thrown in, so releasing up
  // mid-arc does not drag the blade back down through the animation.
  h.aim = state.aim;
  h.timer = 0;
  h.confirmed = false;
  h.hitCount = 0;
  h.impulsed = false;
  h.vaulted = false;
  h.contactTimer = 0;

  // Rhythm: alternating hands fills the meter, spamming one hand does not.
  // Gain is scaled by loadout weight, which is docs/04 §8's "two light weapons
  // trade damage for a faster rhythm meter" — flat 0.24 regardless of what you
  // were holding made that line describe nothing.
  const other = h.hand === 'main' ? state.off : state.main;
  const alternated =
    state.lastHand !== null &&
    state.lastHand !== h.hand &&
    state.clock - other.lastStart <= RHYTHM_WINDOW_MS;
  if (alternated) state.rhythm = clamp(state.rhythm + RHYTHM_GAIN * rhythmGainMul(state), 0, 1);
  else if (state.lastHand === h.hand) state.rhythm = Math.max(0, state.rhythm - 0.04);
  state.lastHand = h.hand;
  h.lastStart = state.clock;

  // Gravity override for the swing, restored when the node ends.
  const g = node.gravityScale ?? h.weapon!.def.gravityScale;
  if (g !== 1) {
    h.savedGravity = w.gravityScale[i];
    w.gravityScale[i] = g;
  }

  if (ctx.onCameraKick) {
    ctx.onCameraKick(cameraKick(h.weapon!.weight) * node.damageMul * 0.35, w.facing[i], 0);
  }
}

function applyImpulse(h: HandState, w: World, i: number): void {
  if (h.impulsed) return;
  h.impulsed = true;
  const n = h.node!;
  // Additive, never assignment: an attack contributes movement, it does not
  // take it over.
  w.vx[i] += n.movement.x * w.facing[i];
  w.vy[i] += n.movement.y;
}

function finishNode(h: HandState, w: World, i: number): void {
  h.chainFrom = h.node;
  h.sinceEnd = 0;
  cancelNode(h, w, i);
}

function cancelNode(h: HandState, w: World | null, i: number): void {
  if (w) restoreGravityNow(h, w, i);
  h.phase = 'idle';
  h.node = null;
  h.timer = 0;
  h.chargeHeld = 0;
  h.chargeMul = 1;
  h.chargeRadiusMul = 1;
  h.chargePrimer = 0;
  h.hitCount = 0;
  h.impulsed = false;
  h.vaulted = false;
  h.contactTimer = 0;
  h.ammoTimer = 0;
}

function restoreGravity(h: HandState, w: World, i: number): void {
  if (h.phase === 'idle') restoreGravityNow(h, w, i);
}

function restoreGravityNow(h: HandState, w: World, i: number): void {
  if (!Number.isNaN(h.savedGravity)) {
    w.gravityScale[i] = h.savedGravity;
    h.savedGravity = NaN;
  }
}

// ------------------------------------------------------------- active frame

function fireOrSwing(
  state: WielderState,
  h: HandState,
  w: World,
  i: number,
  cells: CellWorld,
  ctx: WielderCtx,
  firstFrame: boolean,
): void {
  const wp = h.weapon!;
  const node = h.node!;
  const face = w.facing[i] || 1;
  const hb = node.hitbox;

  // ------------------------------------------------------------------- aim
  //
  // One rotation, and everything directional below is expressed through it: the
  // sweep centre, the hitbox's own offset, the muzzle direction, the summon's
  // heading, the terrain cut, the trail, the spear's plant. `HitboxShape.angle`
  // is already documented as "sweep centre angle relative to facing", so the
  // hook was there — the only thing missing was something to turn it.
  //
  // It is a *replacement*, not an addition. `hb.angle` is where the node's
  // author pointed it; `h.aim` is where the player pointed it, and the player
  // wins. The tempting version — `hb.angle + aim` — reads better and is wrong:
  // a down-air can only be entered by holding down, and every down-air authored
  // at `angle: 1.57` would sum to π and swing backwards through the wielder.
  //
  // `rot` is exactly 0 whenever no vertical is held, so the neutral case is
  // bit-identical to the game before aiming existed rather than merely close.
  const rot = Number.isNaN(h.aim) ? 0 : h.aim - hb.angle;
  const ang = hb.angle + rot;
  const rc = Math.cos(rot);
  const rs = Math.sin(rot);
  // The offset turns with the frame, so a weapon held out in front of the body
  // is held out *above* it when aimed up. `ox` is along facing and `oy` is
  // down-positive, so this is an ordinary rotation in the authored local frame,
  // mirrored into the world by `face` on the way out — the same mirror the
  // shapes get, and the reason aiming straight up or down is face-independent
  // (`cos(±π/2)` is 0, so `face` multiplies nothing).
  const cx = w.x[i] + (hb.ox * rc - hb.oy * rs) * face;
  const cy = w.y[i] + (hb.ox * rs + hb.oy * rc);

  if (node.fire !== undefined) {
    if (firstFrame) {
      // Billed before the round leaves the muzzle, so a weapon can never fire a
      // shot the magazine did not have.
      spendAmmo(h, node);
      if (wp.def.heatPerShot) addHeat(h, wp.def.heatPerShot, w, i);
      if (wp.def.class === 'drone') {
        // The summoner's signature: the press does not put a round in the pool,
        // it puts a body in the world. Routed here rather than inside the fire
        // handler because a `FireSpec` describes a projectile and a drone is
        // not one — see `SummonEvent`.
        if (ctx.onSummon) {
          scratchSummon.state = state;
          scratchSummon.hand = h.hand;
          scratchSummon.owner = i;
          scratchSummon.weapon = wp;
          scratchSummon.node = node;
          scratchSummon.x = cx;
          scratchSummon.y = cy;
          scratchSummon.dirX = Math.cos(ang) * face;
          scratchSummon.dirY = Math.sin(ang);
          scratchSummon.count = Math.max(1, node.fire.count + wp.projectile.countAdd);
          scratchSummon.charge = h.chargeMul;
          ctx.onSummon(scratchSummon);
        }
      } else if (ctx.onFire) {
        // Direction comes from the aimed hitbox angle, mirrored by facing.
        //
        // This comment used to credit the *authored* angle with making "the
        // scattergun's down-air actually fire downward". It never did:
        // `breach_scattergun`'s down-air inherits `angle: 0` from its base, so
        // the weapon whose description is "firing downward is a double jump
        // that also kills things" fired sideways. Aim is what makes the
        // sentence true, and it does so without touching the catalogue — a
        // down-air is only reachable by holding down, which is the aim.
        scratchFire.state = state;
        scratchFire.hand = h.hand;
        scratchFire.attacker = i;
        scratchFire.weapon = wp;
        scratchFire.node = node;
        scratchFire.x = cx;
        scratchFire.y = cy;
        scratchFire.dirX = Math.cos(ang) * face;
        scratchFire.dirY = Math.sin(ang);
        scratchFire.charge = h.chargeMul;
        scratchFire.chargePrimer = h.chargePrimer;
        scratchFire.windows = triggerWindows(state);
        ctx.onFire(scratchFire);
      }
      applyRecoil(h, w, i, node, face, ang);
      // A projectile weapon still touches the world: muzzle heat, sprayed
      // matter, the crater a launcher leaves. §9 admits no exceptions. The
      // muzzle steps along the aim, not along facing, or a shot fired upward
      // would scorch the wall beside the shooter instead of the one above.
      applyTerrain(
        cells,
        wp.terrain,
        cx + Math.cos(ang) * face * 6,
        cy + Math.sin(ang) * 6,
        ctx.rng,
      );
    }
    // A `spawn` hitbox has no melee shape by definition, so there is nothing
    // left to sweep. Anything else (the sprayer's cone, the beam's line) keeps
    // going and does both.
    if (hb.kind === 'spawn') return;
  }

  tryVault(state, h, w, i, cells, cx, cy, face, ang, ctx);

  // The corpse harvest, and the only place in the game a pouch can fill. It runs
  // *before* `applyTerrain` because it takes the cells that pass rather than
  // destroying them — see `harvest.ts` for why this is the scythe's alone.
  if (firstFrame && wp.def.class === 'scythe' && ctx.pouch) {
    const pouch = ctx.pouch();
    if (pouch) harvestSwing(cells, pouch, wp.terrain, cx, cy);
  }

  // Melee terrain interaction runs on the first active frame only, so a long
  // active window (drill, beam) does not multiply destruction by frame rate.
  //
  // It lands where the *hitbox* is, not where the wielder is. This used to pass
  // `cx, cy` — the hitbox's origin, i.e. the player's own body — while
  // `ashen_sword`'s arc reaches 20 units forward. A radius-5 dig centred on a
  // player standing 6 units from a wall cleared five cells of the air they were
  // already standing in and never touched the wall. Digging is the primary
  // traversal verb of the genre, and it did not work at all: the prologue is
  // bisected by a rusted-iron pillar with no gap, and the run ended there.
  //
  // The offset is clamped to stay adjacent to the wielder rather than tracking
  // the full arc. A whip reaching 64 units should not open a hole 32 units away
  // with solid rock in between — the hole a swing makes has to be one you can
  // walk into, or it is a ranged attack rather than a way through.
  if (firstFrame) {
    const dig = wp.terrain.destroy.radius;
    const forward =
      hb.kind === 'box' || hb.kind === 'point'
        ? 0
        : Math.min(hb.radius * 0.5, w.halfW[i] + dig);
    // A swing is a *sweep*, so the cut follows it. One circle at the middle of
    // the arc opens a hole about `2 * dig` tall — ten cells for the starting
    // sword — and the player is twenty-two. The prologue tunnel proved it: you
    // could cut thirteen cells deep into the pillar and still not fit inside
    // the hole you had made, which reads as the dig being broken rather than as
    // the hole being small.
    //
    // Sampling the sweep's ends as well as its middle carves the shape the
    // player watched the weapon trace. A narrow hitbox collapses the three
    // samples together and costs nothing extra; a wide one opens a doorway.
    const swept = hb.halfAngle > 0.05 && hb.kind !== 'box' && hb.kind !== 'point';
    const arcs = swept ? [-hb.halfAngle, 0, hb.halfAngle] : [0];
    // ...but it never undercuts the ground the wielder is standing on. The
    // downward end of a horizontal sweep reaches below the feet, and a player
    // whose floor crumbles every time they attack forward is being punished for
    // using the verb. Digging *down* is a real thing the game wants, and it is
    // reached by a down-angled node — or, now, by *aiming* down, which is the
    // point of the whole feature: `ang` lifts this floor with it, so the rule
    // costs nothing a player deliberately pointed at their own feet.
    const floorY = w.y[i] + w.halfH[i] - dig;
    for (const da of arcs) {
      const a = ang + da;
      applyTerrain(
        cells,
        wp.terrain,
        cx + Math.cos(a) * face * forward,
        Math.min(cy + Math.sin(a) * forward, floorY + Math.max(0, Math.sin(ang) * forward)),
        ctx.rng,
      );
    }
  }
  if (wp.melee.trailMat !== VOID && firstFrame) {
    layTrail(cells, cx, cy, hb, face, rot, wp.melee.trailMat);
  }

  // Continuous is a property of the weapon class, not of how long one node
  // happens to run. Inferring it from `active > 220` swept up `flail_chain`'s
  // heavy — a common-rarity ×2.5 swing — which then re-hit every substep with
  // no dedup for hundreds of damage.
  const continuous = (wp.def.tags & WeaponTag.CONTINUOUS) !== 0;
  if (continuous && h.contactTimer > 0) return;

  const n = collectTargets(w, i, cells, hb, cx, cy, face, rot, reachScale(wp, h));
  let landed = false;

  for (let k = 0; k < n; k++) {
    const t = scratchTargets[k];
    if (alreadyHit(h, t)) continue;
    if (h.hitCount >= MAX_HITS_PER_NODE) break;

    scratchHit.attacker = i;
    scratchHit.target = t;
    scratchHit.weapon = wp;
    scratchHit.node = node;
    scratchHit.hand = h.hand;
    scratchHit.hx = w.x[t];
    scratchHit.hy = w.y[t];
    const ddx = w.x[t] - w.x[i];
    const ddy = w.y[t] - w.y[i];
    const dl = Math.hypot(ddx, ddy) || 1;
    scratchHit.dirX = ddx / dl;
    scratchHit.dirY = ddy / dl;
    scratchHit.stats = state.stats;
    scratchHit.rhythm = state.rhythm;
    scratchHit.charge = h.chargeMul;
    scratchHit.backstab = w.facing[t] === w.facing[i] && wp.def.class === 'dagger';
    scratchHit.continuous = continuous;
    scratchHit.windows = triggerWindows(state);
    scratchHit.chargePrimer = h.chargePrimer;

    const res = resolveHit(w, cells, scratchHit, scratchResult, ctx.rng);
    if (!res.hit) continue;

    if (!continuous) {
      h.hits[h.hitCount++] = t;
    }
    landed = true;
    onConfirm(state, h, w, i, res, node, ctx, t);
  }
  // Sustained contact re-arms on wall time, so the drill's damage rate is the
  // same at 30 Hz and 120 Hz.
  if (continuous && landed) h.contactTimer = CONTINUOUS_HIT_MS;

  // Down-air pogos off terrain too — a whiffed pogo over stone should still
  // bounce, which is what makes it a traversal move rather than a combo move.
  if (node.pogo && !h.confirmed && solidBelow(w, cells, i)) {
    doPogo(state, h, w, i, ctx, -1);
  }
}

function onConfirm(
  state: WielderState,
  h: HandState,
  w: World,
  i: number,
  res: HitResult,
  node: ComboNode,
  ctx: WielderCtx,
  target: number,
): void {
  h.confirmed = true;
  state.canJumpCancel = true;
  if (node.pogo) doPogo(state, h, w, i, ctx, target);
  if (res.detonated !== 0) fireTriggers(state, h, 'onDetonate');
  if (res.crit) fireTriggers(state, h, 'onCrit');
  if (res.killed) {
    fireTriggers(state, h, 'onKill');
    feedHunger(state, HUNGER_PER_KILL);
  }
  bankCharge(h);
  refillOtherHand(state, h);
  scheduleEcho(state, h, target, node);
  applyGrab(h, w, i, target, res);
  fireTriggers(state, h, 'onHit');
  ctx.onHitConfirm(state, h.hand, target, res);
}

/**
 * The two classes for which a hit is a *grab* rather than a shove — and the
 * one difference between them, which is the whole of the Anchor's identity.
 *
 * "Grabs items, tethers to anchors" is the whip's class blurb, and both halves
 * are here. Anything light enough is reeled toward the wielder — the knockback
 * the hit just applied is undone first, so this is genuinely a reversal and not
 * a shove with a smaller number — and anything heavy enough is an anchor, so
 * the whip pulls its wielder toward it instead.
 *
 * **`anchor` takes the first half and refuses the second, deliberately.** Not
 * for flavour: the self-pull is the input to a measured defect. `pickEntryKind`
 * promotes any press taken above 200 u/s to the **dash** entry, so a weapon
 * that reels its own wielder selects its own dash node on the next press — and
 * `tether_whip` lands 285 of 287 of its hits on one node because of it (#50,
 * docs/15 §8). An anchor that hauled itself toward what it hit would reproduce
 * that exactly, so it does not: for `anchor`, a body too heavy to haul is
 * simply not hauled, and `w.vx[i]` is never written on any path.
 *
 * The whip pulls its wielder to the world; the Anchor pulls the world to its
 * wielder. That reads as two weapons rather than one, and it is also why one of
 * them cannot fall into the other's hole.
 */
function applyGrab(h: HandState, w: World, i: number, target: number, res: HitResult): void {
  const cls = h.weapon!.def.class;
  if ((cls !== 'whip' && cls !== 'anchor') || target < 0) return;
  const dx = w.x[i] - w.x[target];
  const dy = w.y[i] - w.y[target];
  const d = Math.hypot(dx, dy) || 1;
  const nx = dx / d;
  const ny = dy / d;

  if (w.mass[target] >= w.mass[i] * TETHER_MASS_RATIO) {
    // The anchor-mass branch, and the only place the two classes diverge.
    if (cls === 'anchor') return;
    w.vx[i] -= nx * GRAB_REEL;
    w.vy[i] -= ny * GRAB_REEL * 0.5;
    return;
  }
  // Same gate `resolveHit` puts on knockback: a body that ignores gravity
  // ignores being pulled around too.
  if ((w.bodyFlags[target] & BodyFlag.NO_GRAVITY) !== 0) return;
  w.vx[target] -= res.knockX;
  w.vy[target] -= res.knockY;
  const massMul = 1 / Math.max(0.25, w.mass[target]);
  w.vx[target] += nx * GRAB_REEL * massMul;
  w.vy[target] += ny * GRAB_REEL * massMul * 0.35;
}

/**
 * The spear's signature: a thrust whose tip lands in solid terrain plants, and
 * the plant throws its wielder.
 *
 * "Thrusts hold position instead of stepping. Planting it in terrain gives a
 * vault" is the Vaulting Pike's own description, and the second sentence had no
 * code — the class was a long box that stepped less. This is traversal *from a
 * weapon*, which is the strongest overlap the catalogue has with the movement
 * pillar, and it is the only way in the game to convert a horizontal swing into
 * height. The down-air is excluded: that node already pogos, and a pogo that
 * also vaulted would be two launches for one input.
 *
 * Velocity is written here rather than handed to the controller, exactly as
 * `doPogo` does — combat owns the impulse, movement observes `state.vaulted`.
 */
function tryVault(
  state: WielderState,
  h: HandState,
  w: World,
  i: number,
  cells: CellWorld,
  cx: number,
  cy: number,
  face: number,
  /** Aimed sweep centre — `fireOrSwing`'s `ang`, not the authored angle. */
  ang: number,
  ctx: WielderCtx,
): void {
  if (h.weapon!.def.class !== 'spear' || h.vaulted) return;
  const node = h.node!;
  if (node.pogo) return;
  const hb = node.hitbox;
  const tipX = Math.round(cx + Math.cos(ang) * hb.radius * face);
  const tipY = Math.round(cy + Math.sin(ang) * hb.radius);
  if (!cells.isSolid(tipX, tipY)) return;

  h.vaulted = true;
  state.vaulted = true;
  w.vy[i] = -VAULT_SPEED;
  w.vx[i] += face * VAULT_PUSH;
  // A plant is a confirm: it refreshes dash and Airstep for the same reason a
  // hit does (docs/02 §2.6 — aggression is mobility), and the pole is the one
  // thing you can confirm against in an empty room.
  ctx.onHitConfirm(state, h.hand, -1, scratchResult);
}

// ------------------------------------------------------------------ echoes

/**
 * Owe a delayed duplicate of the blow that just landed.
 *
 * `ModuleEffect.melee.echoDelayMs` is authored on two modules — the Void
 * Phasecut Core and the whole premise of the temporal family, the Echo Core —
 * folded by `resolve.ts` into `ResolvedWeapon.melee.echoDelayMs`, and until now
 * read by nobody, so a module carrying it was strictly worse than one that did
 * not: exactly the §9 violation issue #15 is about.
 */
function scheduleEcho(state: WielderState, h: HandState, target: number, node: ComboNode): void {
  const delay = h.weapon!.melee.echoDelayMs;
  if (delay <= 0 || target < 0) return;
  let slot = state.echoes.find((e) => !e.active);
  if (!slot) {
    // Overwrite the one closest to landing rather than dropping the new debt:
    // a full pool means the player is mid-flurry, and the oldest echo is the
    // one whose original blow is furthest in the past.
    slot = state.echoes.reduce((a, b) => (a.timer <= b.timer ? a : b));
  }
  slot.active = true;
  slot.timer = delay;
  slot.target = target;
  slot.hand = h.hand;
  slot.damageMul = node.damageMul * h.chargeMul * ECHO_DAMAGE_FRAC;
}

/**
 * Land whatever is due. The strike resolves at the target's *current* position
 * — "wherever the target has moved to", which is what the Echo Core promises
 * and what makes it a worse pick against anything mobile.
 */
function tickEchoes(
  state: WielderState,
  w: World,
  i: number,
  cells: CellWorld,
  ms: number,
  ctx: WielderCtx,
): void {
  for (const e of state.echoes) {
    if (!e.active) continue;
    e.timer -= ms;
    if (e.timer > 0) continue;
    e.active = false;

    const h = e.hand === 'main' ? state.main : state.off;
    const wp = h.weapon;
    const t = e.target;
    // The weapon that owed the echo may have been unequipped, and the target
    // may have died in the meantime. Both are ordinary, and both just cancel it.
    if (!wp || t < 0 || w.alive[t] !== 1 || w.hp[t] <= 0) continue;

    scratchHit.attacker = i;
    scratchHit.target = t;
    scratchHit.weapon = wp;
    // No node: an echo repeats the *blow*, not the swing, so it applies no
    // primer of its own and its damage scalar rides on `charge` instead.
    scratchHit.node = null;
    scratchHit.hand = e.hand;
    scratchHit.hx = w.x[t];
    scratchHit.hy = w.y[t];
    const ddx = w.x[t] - w.x[i];
    const ddy = w.y[t] - w.y[i];
    const dl = Math.hypot(ddx, ddy) || 1;
    scratchHit.dirX = ddx / dl;
    scratchHit.dirY = ddy / dl;
    scratchHit.stats = state.stats;
    scratchHit.rhythm = state.rhythm;
    scratchHit.charge = e.damageMul;
    scratchHit.backstab = false;
    // Iframes are the window a *blow* opens. An echo arriving inside one is the
    // normal case — it is 400 ms behind a hit that granted 190 ms of them — so
    // it resolves as sustained contact does: no iframe check, no iframe grant,
    // and no hitstop, because a freeze with nothing on screen causing it reads
    // as a hitch.
    scratchHit.continuous = true;
    scratchHit.windows = triggerWindows(state);
    scratchHit.chargePrimer = 0;
    scratchHit.echo = true;

    const res = resolveHit(w, cells, scratchHit, scratchResult, ctx.rng);
    if (!res.hit) continue;
    if (res.killed) {
      fireTriggers(state, h, 'onKill');
      feedHunger(state, HUNGER_PER_KILL);
    }
    ctx.onHitConfirm(state, e.hand, t, res);
  }
}

/** Rebound and refresh Airstep. Legal off enemies, solids, and dense liquids. */
function doPogo(
  state: WielderState,
  h: HandState,
  w: World,
  i: number,
  ctx: WielderCtx,
  target: number,
): void {
  if (state.pogoed) return;
  state.pogoed = true;
  w.vy[i] = -POGO_SPEED;
  state.canJumpCancel = true;
  ctx.onHitConfirm(state, h.hand, target, scratchResult);
}

function applyRecoil(
  h: HandState,
  w: World,
  i: number,
  node: ComboNode,
  face: number,
  /** Aimed sweep centre, so recoil pushes opposite where the shot went. */
  ang: number,
): void {
  const f = node.fire;
  if (!f || f.recoil === 0) return;
  const r = f.recoil * h.weapon!.projectile.recoilMul;
  // Recoil-as-mobility: it pushes opposite the muzzle, including downward
  // shots, which is exactly how the scattergun doubles as a jump. Reading the
  // *aimed* angle rather than the authored one is what finally connects that
  // sentence to the weapon it was written about — `breach_scattergun`'s down-air
  // is authored `angle: 0`, so the boost had never once fired.
  w.vx[i] -= face * r;
  if (ang > 1.0) w.vy[i] -= r;
}

/**
 * Fire an event. Scalar payloads are not applied here — they are read at hit
 * time from the live event mask, which is what makes a *conditional* module
 * conditional. What this does is latch the windows for the three events that
 * are states rather than instants, and pay out the meter side.
 */
function fireTriggers(state: WielderState, h: HandState, event: string): void {
  const wp = h.weapon;
  if (!wp) return;
  if (event === 'onKill') state.killWindow = TRIGGER_WINDOW_MS;
  for (const t of wp.triggers) {
    if (t.event !== event) continue;
    if (t.effect.cadenceMul) state.rhythm = clamp(state.rhythm + 0.05, 0, 1);
  }
}

/**
 * How far this hand's hitbox actually reaches.
 *
 * `collectTargets` used the raw authored `hb.radius`, so `ResolvedWeapon.reach`
 * — which carries both the per-drop `reachMul` roll and every module's
 * `reachMul` — was display-only: a reach module grew the drawn blade and not
 * the hitbox. Expressed as a ratio against the authored reach so per-node
 * radii (the greatblade's wider heavy, the lance's dash) keep their relative
 * shape.
 */
function reachScale(wp: ResolvedWeapon, h: HandState): number {
  const base = wp.def.reach > 0 ? wp.reach / wp.def.reach : 1;
  return base * wp.melee.arcMul * h.chargeRadiusMul;
}

// ---------------------------------------------------------- hit collection

function alreadyHit(h: HandState, t: number): boolean {
  for (let k = 0; k < h.hitCount; k++) if (h.hits[k] === t) return true;
  return false;
}

/**
 * Gather living, hostile entities overlapping the hitbox. Writes indices into
 * the module-level scratch buffer and returns the count — this runs several
 * times per frame per hand and must not allocate.
 */
function collectTargets(
  w: World,
  self: number,
  cells: CellWorld,
  hb: HitboxShape,
  cx: number,
  cy: number,
  face: number,
  /** Aim rotation of the hitbox frame; 0 when the player held no vertical. */
  rot: number,
  scale: number,
): number {
  const reach = hb.radius * scale;
  const reach2 = (reach + 24) * (reach + 24);
  const team = w.team[self];
  let n = 0;

  w.each(Comp.HEALTH, (j) => {
    if (n >= scratchTargets.length || j === self) return;
    if (w.hp[j] <= 0) return;
    if (w.team[j] === team || w.team[j] === Team.NEUTRAL) return;

    const dx = w.x[j] - cx;
    const dy = w.y[j] - cy;
    if (dx * dx + dy * dy > reach2) return;
    if (!shapeContains(hb, reach, dx, dy, face, rot, w.halfW[j], w.halfH[j])) return;
    if (!hb.throughWalls && !hasLineOfSight(cells, cx, cy, w.x[j], w.y[j])) return;

    scratchTargets[n++] = j;
  });
  return n;
}

function shapeContains(
  hb: HitboxShape,
  reach: number,
  dx: number,
  dy: number,
  face: number,
  rot: number,
  hw: number,
  hh: number,
): boolean {
  // Local space: mirror by facing so every shape is authored facing right, then
  // *un*-rotate by the aim so every shape is authored pointing forward. Two
  // steps of the same trick, and the reason the authored geometry below — the
  // cone's apex correction, the line's near end, `hb.angle` itself — never has
  // to learn that aiming exists. Rotating the shapes instead of the query would
  // mean touching all six cases and getting `back` wrong in two of them.
  const mx = dx * face;
  const my = dy;
  const rc = Math.cos(rot);
  const rs = Math.sin(rot);
  const lx = mx * rc + my * rs;
  const ly = -mx * rs + my * rc;

  switch (hb.kind) {
    case 'box':
      return Math.abs(lx) <= reach + hw && Math.abs(ly) <= hb.thickness + hh;

    case 'circle':
      return Math.hypot(lx, ly) <= reach + Math.max(hw, hh);

    case 'ring': {
      const d = Math.hypot(lx, ly);
      return d <= reach + Math.max(hw, hh) && d >= hb.thickness - Math.max(hw, hh);
    }

    case 'arc':
    case 'cone': {
      // A cone's apex is the wielder, not the hitbox centre.
      //
      // Measuring the angle from a centre sitting `ox` units out in front put a
      // body in contact *behind* the apex — `atan2` reads it at π off axis — so
      // every cone weapon had a dead zone of exactly `ox` at its own feet: the
      // sprayer (ox 10) missed at gaps 0, 4 and 8 and connected only from 12
      // out. This is the same bug the `line` case below was fixed for, and the
      // same fix: measure from the wielder and push the far edge out by `ox` so
      // the reach at distance is unchanged (issue #14).
      //
      // Arcs share the case and are unaffected in practice — a swing is
      // authored around the body, so `ox` is 0 on every arc in the catalogue —
      // but they get the same treatment for the same reason.
      const back = hb.ox > 0 ? hb.ox : 0;
      const ax = lx + back;
      const d = Math.hypot(ax, ly);
      if (d > reach + back + Math.max(hw, hh)) return false;
      if (hb.halfAngle >= Math.PI) return true;
      const a = Math.atan2(ly, ax);
      let da = a - hb.angle;
      while (da > Math.PI) da -= Math.PI * 2;
      while (da < -Math.PI) da += Math.PI * 2;
      // Widen the angular test for close targets, or a body standing on top of
      // the wielder falls into the gap between arcs.
      const slack = Math.atan2(Math.max(hw, hh), Math.max(1, d));
      return Math.abs(da) <= hb.halfAngle + slack;
    }

    case 'line': {
      const ca = Math.cos(hb.angle);
      const sa = Math.sin(hb.angle);
      const along = lx * ca + ly * sa;
      const across = -lx * sa + ly * ca;
      // The shaft starts at the wielder, not at the hitbox centre. Measuring
      // `along` from the centre — which sits `ox` units out in front — carved a
      // dead zone of exactly `ox` in front of every long weapon: a lance
      // (ox 24) missed at gaps 0, 6 and 12 and only connected from 18 out, so
      // an enemy in contact with a lance user was untouchable. Every lance node
      // shares the offset, so it was the whole class.
      const back = hb.ox > 0 ? hb.ox : 0;
      return along >= -back - hw && along <= reach + hw && Math.abs(across) <= hb.thickness + hh;
    }

    case 'point':
      return Math.hypot(lx, ly) <= Math.max(hw, hh) + 2;

    case 'spawn':
    default:
      return false;
  }
}

/** Solid terrain between the swing origin and the target blocks the hit. */
function hasLineOfSight(cells: CellWorld, x0: number, y0: number, x1: number, y1: number): boolean {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dist = Math.hypot(dx, dy);
  if (dist < 8) return true;
  let blocked = 0;
  cells.raycast(x0, y0, dx, dy, dist, (x, y, mat) => {
    if (mat !== VOID && M.solid[mat] === 1) blocked++;
    // A couple of cells of wall is a corner, not cover.
    return blocked > 3;
  });
  return blocked <= 3;
}

function solidBelow(w: World, cells: CellWorld, i: number): boolean {
  const x = Math.floor(w.x[i]);
  const y = Math.floor(w.y[i] + w.halfH[i] + 2);
  if (cells.isSolid(x, y)) return true;
  // Dense liquids count: pogoing off lava is legal, hurts, and is sometimes
  // the correct route (docs/02 §4).
  const m = cells.peek(x, y);
  return m !== VOID && M.density[m] >= 1500;
}

function layTrail(
  cells: CellWorld,
  cx: number,
  cy: number,
  hb: HitboxShape,
  face: number,
  /** Aim rotation of the hitbox frame, so the trail lies where the swing went. */
  rot: number,
  mat: number,
): void {
  const steps = Math.max(3, Math.round(hb.radius / 3));
  for (let k = 0; k < steps; k++) {
    const a =
      hb.angle + rot - hb.halfAngle + (2 * hb.halfAngle * k) / Math.max(1, steps - 1);
    const x = Math.round(cx + Math.cos(a) * hb.radius * face);
    const y = Math.round(cy + Math.sin(a) * hb.radius);
    if (cells.peek(x, y) === VOID) cells.set(x, y, mat);
  }
}

// -------------------------------------------------------------- queries

/**
 * How long the node this hand is running will actually take, in seconds.
 *
 * Presentation needs this for the swing animation. Reading the raw node values
 * gets it wrong in three ways at once — `windupMul`, `recoveryMul` and cadence
 * all move it — and it used to be wrong by a further factor of 2.5 because of
 * the global attack-speed divisor that no longer exists.
 */
/**
 * Which hand's node should drive the swing animation — issue #92.
 *
 * Main wins a tie because it is the hand the camera and the aim already
 * follow; the point of this function is the case where main is *idle*.
 *
 * It exists as a named export rather than an expression inside `Game` because
 * the bug it fixes was exactly that: the integrator selected the swinging hand
 * and then asked `nodeDuration` about `'main'` regardless. With the main hand
 * idle that returns 0, so every off-hand-only swing fell to the caller's 0.26 s
 * fallback — and melee node totals span 58 ms to 488 ms, so a dagger flick was
 * drawn over four times its real length. Selection and lookup now cannot
 * disagree, because there is one call and it is here.
 *
 * **`!== 'idle'` was not the same question as "is running a node" — issue #127.**
 *
 * Three non-idle phases carry `node === null`: `reloading`, `charging` and
 * `parry`. So a main hand mid-reload — a projectile main hand that has just run
 * its magazine dry, which is a second of every magazine's cycle — was selected
 * over an off hand that was genuinely mid-swing, and `nodeDuration` of a hand
 * with no node is 0. That is #92 exactly, reintroduced through the gap between
 * the two predicates rather than through two call sites: measured, the off
 * hand's `flense_dagger:l1` runs 56.8 ms and the caller drew it over the 0.26 s
 * fallback, 4.6× too long. The same hole opens whenever the main hand is
 * holding a charge and the off hand interjects, which is the priming loop
 * `wielder.ts`'s header calls the whole point of two hands.
 *
 * So the tie-break is now in two tiers. A hand that is **running a node** wins
 * over one that is merely busy; main wins between equals, because it is the
 * hand the camera and the aim follow. The last two lines answer the weaker
 * question — "is either hand doing anything at all" — for a caller that has no
 * node to look up either way, and keep this total.
 */
export function swingingHand(state: WielderState): Hand | null {
  if (runningNode(state.main)) return 'main';
  if (runningNode(state.off)) return 'off';
  if (state.main.phase !== 'idle') return 'main';
  if (state.off.phase !== 'idle') return 'off';
  return null;
}

/** Busy *and* with a node to measure — the two are not the same phase set. */
function runningNode(h: HandState): boolean {
  return h.phase !== 'idle' && h.node !== null;
}

/**
 * Take the "this hand began something the body should mime" edge, if one is
 * waiting — issues #137 and #142.
 *
 * **It consumes**, which is why it is `take…` and not `…Started`. There is
 * exactly one reader (`game.ts`'s per-substep announcement) and a second one
 * would silently steal the first one's edge, so the name has to say so at the
 * call site rather than in a docstring nobody opens.
 *
 * ---------------------------------------------------------------------------
 * WHAT #137 ESTABLISHED, AND WHY IT SURVIVES INTO THIS SHAPE
 * ---------------------------------------------------------------------------
 *
 * The distinction `swingingHand` draws between "running a node" and "merely
 * busy" has an edge-triggered twin, and its absence is what made one press
 * produce two swings. The caller used to ask
 * `phaseBefore === 'idle' && phase !== 'idle'`, which is **not** "an attack
 * started": `reloading` and `charging` are non-idle, so a magazine running dry
 * announced a swing the player never asked for, and a charge weapon announced
 * its swing when the *hold* opened and then stayed silent for the blow that
 * actually landed. Measured through a real `Game`: one off-hand press that
 * empties the magazine produced **two** `noteAttack` calls.
 *
 * The latch keeps that exactly: it is raised by `startNode` and `startParry`
 * and by nothing else, so a wait cannot raise it however non-idle it looks. A
 * parry raises it because a parry *is* a thrown verb (docs/04 §4).
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS A LATCH AND NOT THE DERIVED EDGE IT REPLACES — #142
 * ---------------------------------------------------------------------------
 *
 * This used to be `(phase === 'windup' || phase === 'parry') && timer === 0`,
 * which is a true statement about a hand and a **false** one about the game,
 * for the reason every derived edge eventually is: it is only observable by
 * whoever looks before the next tick. `game.ts` polls in `stepMovement`
 * *after* `updateWielder` has ticked the hand. A node started from inside
 * `updateWielder` is caught, because the poll follows it in the same pass. The
 * counter node a parry opens is started from `resolveIncomingBlow`, on the
 * **enemy step** in `stepSimulation` — after that frame's movement substeps
 * have already polled — so the next poll only arrives on the far side of
 * another `updateWielder`, by which time `timer` has taken a `dt` and the edge
 * is gone.
 *
 * **What that cost, measured through the shipped binding**, with the parry
 * thrown 117 ms ahead of the blow it meets (inside the sword's 130 ms window,
 * which is how a parry is thrown at all — it is a read): four of the counter's
 * eight live-hitbox frames stamped **no weapon quads at all**, and the four
 * that did were the tail of the parry's own 0.26 s fallback pose running out.
 * The counter — which is the entire reward for the read (docs/04 §3.1) — dealt
 * its damage while the player watched the parry end. `tests/hitCredit.test.ts`
 * §8 holds the frame table.
 *
 * **The alternative was to reorder `stepMovement`** so the poll runs before
 * `updateWielder`, and it was not taken. It is cheaper, and it fixes this
 * caller rather than the class: the counter is only the *first* out-of-band
 * starter, and the next one — a scripted riposte, a trap, an echo — would
 * arrive at the same silence and read as a new bug. Reordering that function is
 * also not free; #122's field placement and #42's frame order are both
 * load-bearing there and documented as such. A latch makes the edge a fact
 * about the node instead of a fact about when somebody looked, which is the
 * only version of this that a future caller cannot get wrong by construction.
 *
 * **It is not cleared by `cancelNode`,** deliberately. A node that started and
 * was cancelled before anyone drew it still *started* — `startNode` has already
 * spent its camera kick and its rhythm gain — and swallowing the announcement
 * would put the same "invisible swing" back for a narrower case. The latch is a
 * boolean and not a count for the mirrored reason: two starts inside one
 * substep are one thing to draw, because the body has one pose.
 */
export function takeSwingStart(h: HandState): boolean {
  const pending = h.swingPending;
  h.swingPending = false;
  return pending;
}

/**
 * `drawnHand` was here, and it is deleted rather than fixed — issue #139.
 *
 * It answered *which weapon is being held*: the main hand keeps the body's one
 * weapon slot at rest and yields it only while the off hand alone is running a
 * node. That is a true and useful sentence, and it is not the question the slot
 * is asking. The slot is drawn along the attack pose, so what belongs in it is
 * **the weapon that is swinging** — a decision made on the swing edge, about an
 * instant, which then has to persist for the length of the pose. A projectile
 * node is over in three substeps while the pose it announced is still running,
 * so any function of the *current* hand states hands the slot back mid-swing:
 * measured, that drew 16 quads of the main hand's hammer on a press of the
 * off-hand gun. `game.ts` now latches the hand on the edge and holds it against
 * `attackT`; see the comment beside `bodies.setWeapon`.
 *
 * This note is kept where the function was because #92, #127, #137 and #139 are
 * four attempts to derive that latch from state, and the fifth would start by
 * reaching for a helper with this name.
 */

export function nodeDuration(state: WielderState, hand: Hand): number {
  const h = hand === 'main' ? state.main : state.off;
  if (!h.node || !h.weapon) return 0;
  return (nodeWindup(h) + nodeActive(h) + nodeRecovery(state, h)) / 1000;
}

/**
 * The aim rotation of the swing currently in flight, in the local frame every
 * hitbox and every swing pose is authored in (facing right, +y down). Zero when
 * nothing is swinging, or when the swing was thrown with no vertical held.
 *
 * Presentation only, and it exists because the renderer cannot derive it.
 * `weaponVisual.swingPose` animates a *style* against a progress value — it has
 * no wielder to ask where the player was pointing — so a blade drawn from it
 * alone stays horizontal while the hitbox that is actually cutting swings
 * overhead. A player watching a level swing open a hole in the ceiling will read
 * the *dig* as broken, which is precisely the failure `0f5569a` was about.
 *
 * **It is `h.aim`, not `h.aim - h.node.hitbox.angle` — issue #112.**
 *
 * The subtraction was here for a year and it was a correction for something that
 * never happened. `fireOrSwing` needs it, because there the aim *replaces* the
 * authored angle: `ang = hb.angle + (aim - hb.angle)` is `aim`, and the term is
 * the difference between where the author pointed the hitbox and where the
 * player pointed it. The drawing has no such author. `swingPose` takes a style
 * and a `t`, it has never seen the node, and all ten of its curves are authored
 * about the horizontal — so subtracting the node's hitbox angle here removed a
 * rotation that was not in the pose.
 *
 * On the 199 nodes authored `angle: 0` the two formulas are the same number,
 * which is why this survived: the only test that read this function used
 * `ashen_sword`, every node of which is one of those 199. The four that author
 * it non-zero are all `dair` — `pike_spear:da`, `iron_greatblade:da`,
 * `kiln_beam:da`, `spray_nozzle:da`, at 1.4–1.57 — and a `dair` can only be
 * entered by holding down, so on exactly the nodes a player is guaranteed to be
 * aiming at, the rotation came out 1.57 − 1.57 = 0 and the weapon was drawn
 * **horizontal** while its hitbox pointed at the floor. Aimed down-diagonal it
 * came out 0.785 − 1.57 and the weapon was drawn 45° *above* level: a 90°
 * inversion between the down aim and the up aim. `tests/drawnAim.test.ts` §2
 * measures both, and the two projectile nodes in that list draw nothing.
 *
 * So the drawn weapon is the pose turned by the aim, and the hitbox is the aim.
 * Both are the same latch and neither reads the other's authored angle.
 */
export function swingAimRotation(state: WielderState): number {
  // The same selector `swingingHand` is, and not a second copy of it — this
  // function used to spell the choice out again, and the two spellings then
  // disagreed about `reloading` and `charging` (#127). One selector is the
  // whole of #92's ruling; a second copy of it is how the ruling gets undone.
  const hand = swingingHand(state);
  const h = hand === 'main' ? state.main : hand === 'off' ? state.off : null;
  if (!h || !h.node || Number.isNaN(h.aim)) return 0;
  return h.aim;
}

/**
 * The world angle a drawn weapon leaves the grip at.
 *
 * The whole of `game.ts:drawWeapons`'s orientation arithmetic, exported so a
 * test measures the expression that ships rather than a copy of it — the copy
 * being how #112 stayed invisible while `tests/aiming.test.ts` grew 31 cases
 * around it.
 *
 * `swingAngle` is `SwingPose.angle`, authored facing right; `aimRot` is
 * `swingAimRotation`; the mirror is a reflection about the vertical rather than
 * a negation, so a weapon swung by a left-facing wielder keeps its vertical
 * sense and only changes side.
 */
export function drawnSwingAngle(swingAngle: number, aimRot: number, face: number): number {
  const local = swingAngle + aimRot;
  return face < 0 ? Math.PI - local : local;
}

/**
 * The drawn weapon's spine: `segments + 1` joint positions, `x, y` interleaved
 * into `out`, starting at the pivot and ending at the tip. Returns the number of
 * points written.
 *
 * The renderer strokes between consecutive points; a test reads the last one,
 * which is the only way to ask a nine-segment whip which way it is pointing —
 * `curl` bends each segment off the last, so a flexible weapon does not point
 * along `angle`.
 */
export function swingSpine(
  originX: number,
  originY: number,
  angle: number,
  curl: number,
  total: number,
  segments: number,
  out: number[],
): number {
  const segs = Math.max(1, segments);
  const segLen = total / segs;
  let px = originX;
  let py = originY;
  let a = angle;
  out[0] = px;
  out[1] = py;
  for (let k = 0; k < segs; k++) {
    px += Math.cos(a) * segLen;
    py += Math.sin(a) * segLen;
    out[k * 2 + 2] = px;
    out[k * 2 + 3] = py;
    a += curl;
  }
  return segs + 1;
}

/** Movement reads this to know whether an attack is committing the character. */
export function isAttacking(state: WielderState): boolean {
  return state.main.phase !== 'idle' || state.off.phase !== 'idle';
}

/**
 * The standing cost of the pair you carry, as a movement multiplier.
 *
 * docs/04 §8 frames weight balance as a property of the *loadout* — "two heavy
 * weapons reduce mobility measurably" — and the rhythm half of that sentence
 * has always been pair-aware, because `rhythmGainMul` reads `loadoutWeight`,
 * which sums both hands. The mobility half read only whichever hand happened to
 * be swinging, so a genuine two-heavy loadout and a heavy+light one were
 * indistinguishable while the heavy hand swung (issue #30). This term is the
 * fix: it is always on, so carrying two hammers is heavy *between* swings too,
 * and the off hand finally matters to traversal.
 *
 * Empty hands are exactly 1. The standing cost is the price of what you chose
 * to carry, never a global movement nerf.
 *
 * Deliberately not exported: `attackDragOf` folds it in, so the movement
 * controller keeps reading exactly one number. Splitting it out would give the
 * caller two things to combine and one of them to forget.
 */
const CARRY_DRAG = 0.18;

function carryDragOf(state: WielderState): number {
  if (!state.main.weapon && !state.off.weapon) return 1;
  return 1 - loadoutWeight(state) * CARRY_DRAG;
}

/**
 * Combined drag the movement controller should honour this frame: the standing
 * cost of the pair, times the per-swing cost of whatever is mid-swing.
 *
 * The per-swing half stays a minimum across hands on purpose — one body cannot
 * commit twice, so the heaviest live swing is the one that decides how planted
 * you are. What the minimum was never allowed to do is stand in for the pair,
 * which is what the standing term is for.
 */
export function attackDragOf(state: WielderState): number {
  let d = 1;
  if (state.main.weapon && (state.main.phase === 'windup' || state.main.phase === 'active')) {
    d = Math.min(d, attackDrag(state.main.weapon.weight));
  }
  if (state.off.weapon && (state.off.phase === 'windup' || state.off.phase === 'active')) {
    d = Math.min(d, attackDrag(state.off.weapon.weight));
  }
  return d * carryDragOf(state);
}

/**
 * Two heavy weapons measurably cost mobility; heavy+light is the mobile
 * default; two light weapons trade damage for rhythm (docs/04 §8).
 */
export function loadoutWeight(state: WielderState): number {
  const a = state.main.weapon?.weight ?? 0;
  const b = state.off.weapon?.weight ?? 0;
  return clamp((a + b) * 0.5, 0, 1);
}

export function rhythmGainMul(state: WielderState): number {
  return 1 + (1 - loadoutWeight(state)) * 0.5;
}

/** Convenience for HUD/AI: is the wielder currently staggered out of control? */
export function isStaggered(w: World, i: number): boolean {
  return w.stagger[i] > 0;
}

// ------------------------------------------------------- resource queries

/**
 * Everything the HUD needs to draw one hand's resources.
 *
 * Filled into a caller-owned struct rather than returned fresh, because the
 * adapter reads this twice per frame and the whole point of that file is that
 * the per-frame path does not allocate.
 *
 * `-1` means "this weapon has no such resource" and is the signal to hide the
 * row entirely. That distinction is load-bearing: `ammo` was left null in the
 * adapter precisely because reading a field nothing wrote would have printed
 * `0/0` on all 29 weapons, and printing `0/1` on a weapon with no heat would be
 * the same mistake with a different number.
 */
export interface HandResources {
  /** Rounds left, or -1 for a weapon with no magazine. */
  ammo: number;
  ammoMax: number;
  reloading: boolean;
  /** 0 at the start of the reload, 1 when the magazine is back. */
  reload01: number;
  /** 0..1, or -1 when the weapon cannot heat. */
  heat01: number;
  /** 0..1, or -1 when the class has no charge identity and no charge banked. */
  charge01: number;
  /** What `charge01` means, for the label. Empty when there is nothing to show. */
  chargeKind: '' | ChargeKind;
  /** Set while the hand is locked out cooling down. */
  overheated: boolean;
}

export function newHandResources(): HandResources {
  return {
    ammo: -1, ammoMax: 0, reloading: false, reload01: 1,
    heat01: -1, charge01: -1, chargeKind: '', overheated: false,
  };
}

export function readHandResources(
  state: WielderState,
  hand: Hand,
  out: HandResources,
): HandResources {
  const h = hand === 'main' ? state.main : state.off;
  const wp = h.weapon;
  const inst = h.instance;
  if (!wp || !inst) {
    out.ammo = -1; out.ammoMax = 0; out.reloading = false; out.reload01 = 1;
    out.heat01 = -1; out.charge01 = -1; out.chargeKind = ''; out.overheated = false;
    return out;
  }

  const mag = magazineOf(wp.def);
  out.ammoMax = mag;
  out.ammo = mag > 0 ? Math.max(0, inst.ammo) : -1;
  out.reloading = h.phase === 'reloading';
  const total = wp.def.reloadMs ?? 1000;
  out.reload01 = out.reloading ? clamp(1 - h.reloadTimer / Math.max(1, total), 0, 1) : 1;

  // Heat is shown for anything that can produce it: the class's own sustained
  // fire, or an `overheat` self-risk bolted on by a module.
  const canHeat =
    wp.def.heatPerShot !== undefined ||
    wp.def.heatPerSecond !== undefined ||
    wp.risks.some((r) => r.kind === 'overheat');
  out.heat01 = canHeat ? clamp(inst.heat, 0, 1) : -1;
  out.overheated = h.heatLocked;

  const kind = chargeKindOf(wp.def);
  // A `battery` weapon only has a bar once something is actually filling it —
  // otherwise 25 of 29 weapons would carry a permanently empty gauge.
  const showCharge = kind !== 'battery' || wp.melee.chargePerHit > 0 || inst.charge > 0;
  out.chargeKind = showCharge ? kind : '';
  out.charge01 = showCharge ? clamp(inst.charge, 0, 1) : -1;
  return out;
}

/**
 * Top a weapon back up. This is the free reload a rest node grants (#6) — the
 * loop is "spend the magazine in the room, walk out of it topped up", and a
 * player who has to farm ammo is playing a different, worse game.
 */
export function refillInstance(inst: WeaponInstance, def: WeaponDef): void {
  const mag = magazineOf(def);
  if (mag > 0) inst.ammo = mag;
  inst.heat = 0;
  const kind = chargeKindOf(def);
  if (kind === 'fuel' || kind === 'guard') inst.charge = 1;
}

/** Both hands at once, for the rest-node path. */
export function refillHands(state: WielderState): void {
  for (const h of [state.main, state.off]) {
    if (!h.instance || !h.weapon) continue;
    refillInstance(h.instance, h.weapon.def);
    h.heatLocked = false;
    if (h.phase === 'reloading') h.phase = 'idle';
  }
}
